import {
  eachDayOfInterval,
  format,
  startOfDay,
  endOfDay,
  parseISO,
  addHours,
  differenceInMinutes,  
  isBefore,
  subHours,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import moment from "moment";
import {
  OCCUPANCY_STATUSES,
  RESERVATION_STATUSES,
  STAY_TYPES,
} from "./Constants";
import numWords from "num-words";
import { decodeJwt } from "jose";
import i18next from "i18next";
import { warnToster } from "../shared/toster/Toster";

const Decimal = require("decimal.js");

export class Helper {
  cleanNumber = (value) => String(value ?? "").replace(/\D/g, "");

  scrollToFirstError = (errors) => {
    const toVisible = (el) =>
      (el &&
        el.closest &&
        el.closest(
          ".MuiFormControl-root, .MuiTextField-root, .MuiAutocomplete-root"
        )) ||
      el;

    // Expand any collapsed MUI accordion(s) the field lives inside, outermost
    // first, so the field can actually be revealed/scrolled to.
    const expandAncestorAccordions = (el) => {
      const summaries = [];
      let acc = el.closest && el.closest(".MuiAccordion-root");
      while (acc) {
        const summary = acc.querySelector(
          ":scope > .MuiAccordionSummary-root"
        );
        if (summary && summary.getAttribute("aria-expanded") === "false") {
          summaries.push(summary);
        }
        acc =
          acc.parentElement && acc.parentElement.closest
            ? acc.parentElement.closest(".MuiAccordion-root")
            : null;
      }
      summaries.reverse().forEach((s) => s.click());
      return summaries.length > 0;
    };

    const run = () => {
      // 1) Gather DOM elements from every error that carries a real ref.
      const refs = [];
      const collect = (node) => {
        if (!node || typeof node !== "object") return;
        if (node.ref && typeof node.ref.getBoundingClientRect === "function") {
          refs.push(node.ref);
          return;
        }
        Object.keys(node).forEach((k) => collect(node[k]));
      };
      if (errors) collect(errors);

      let candidates = refs.map(toVisible).filter(Boolean);

      // 2) Fallback to anything MUI has marked as errored.
      if (!candidates.length) {
        candidates = Array.from(
          document.querySelectorAll('[aria-invalid="true"], .Mui-error')
        )
          .map(toVisible)
          .filter(Boolean);
      }
      candidates = [...new Set(candidates)];
      if (!candidates.length) return false;

      // 3) First error in DOM (source) order — stays correct even when a field
      // is hidden inside a collapsed accordion (rect-based ordering would not).
      candidates.sort((a, b) =>
        a === b
          ? 0
          : a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : 1
      );
      const target = candidates[0];

      const expanded = expandAncestorAccordions(target);

      const doScroll = () => {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable =
          target.querySelector("input, textarea, select, button") || target;
        try {
          focusable.focus?.({ preventScroll: true });
        } catch {
          focusable.focus?.();
        }
      };

      // Wait out the accordion expand animation before scrolling.
      if (expanded) setTimeout(doScroll, 400);
      else doScroll();
      return true;
    };

    // Defer so MUI has applied error classes after React re-renders; retry
    // once in case the first paint hasn't landed yet.
    setTimeout(() => {
      if (!run()) setTimeout(run, 200);
    }, 50);
  };

  orderRoomsByNumberAsc = (a, b) =>
    String(a?.room_no || "").localeCompare(String(b?.room_no || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });

  filterRatePlansForRoomType = (
    ratePlans = [],
    roomTypeId,
    selectedRatePlanId,
    isCmBooking = null
  ) => {
    const selectedRoomTypeId = Number(roomTypeId);
    const selectedRatePlan = Number(selectedRatePlanId);
    const targetVisibility = isCmBooking === true ? "OTA" : isCmBooking === false ? "DIRECT" : null;

    return ratePlans.filter((ratePlan) => {
      const isSelected = Number(ratePlan?.rate_plan_id) === selectedRatePlan;
      const isActive = ratePlan?.is_active !== false;
      const matchesVisibility = targetVisibility === null || ratePlan?.channel_visibility === targetVisibility;
      const hasRoomType = ratePlan?.room_type_rates?.some((rate) => {
        const mappedRoomTypeId =
          rate?.room_type_id ?? rate?.room_type?.room_type_id;
        return Number(mappedRoomTypeId) === selectedRoomTypeId;
      });

      return (isActive || isSelected) && matchesVisibility && hasRoomType;
    });
  };

  async handleFormValidation(data, context, validationSchema, setError) {
    try {
      // Validate the entire form using Yup schema
      await validationSchema.validate(data, { abortEarly: false });
      return {
        values: data,
        errors: {},
      };
    } catch (validationErrors) {
      // Map Yup validation errors to react-hook-form errors
      const mappedErrors = validationErrors.inner.reduce((acc, error) => {
        acc[error.path] = {
          type: error.type || "validation",
          message: error.message,
        };
        return acc;
      }, {});

      // Set errors using setError
      Object.keys(mappedErrors).forEach((field) => {
        setError(field, mappedErrors[field]);
      });

      return {
        values: {},
        errors: mappedErrors,
      };
    }
  }

  showNotification = (title, body, icon, onClick) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const notification = new Notification(title, {
        body,
        icon: icon || "/favicon.ico",
        tag: "whatsapp-message",
      });
      if (onClick) {
        notification.onclick = () => {
          window.focus();
          onClick();
        };
      }
      setTimeout(() => notification.close(), 5000);
    }
  };

  getDate() {
    const startDate = () => {
      return format(startOfDay(new Date()), "yyyy-MM-dd HH:mm:ss");
    };

    const endDate = () => {
      return format(endOfDay(new Date()), "yyyy-MM-dd HH:mm:ss");
    };

    const getTimeFromDate = (dateString) => {
      try {
        const date = new Date(dateString);
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const isPM = hours >= 12;
        const formattedHours = hours % 12 || 12;
        return `${formattedHours}:${minutes < 10 ? "0" : ""}${minutes} ${isPM ? "PM" : "AM"
          }`;
      } catch (error) {
        console.error(`Error Converting Given Date: ${dateString} :`, error);
      }
    };

    const formatDateString = (date, format) => {
      let formattedDate;
      try {
        let dateObject = date instanceof Date ? date : new Date(date);
        switch (format) {
          case "dd-mm-yyyy":
            formattedDate = `${dateObject
              .getDate()
              .toString()
              .padStart(2, "0")}-${(dateObject.getMonth() + 1)
                .toString()
                .padStart(2, "0")}-${dateObject.getFullYear()}`;
            break;
          default:
            formattedDate =
              "Enter A Valid Date Format Available Formats:[dd-mm-yyyy]";
        }
        return formattedDate;
      } catch (error) {
        console.error("Error Formatting Date:", error?.message || error);
      }
    };

    const calculateDaysDifference = (startDateString, endDateString) => {
      try {
        const startDate = new Date(startDateString);
        const endDate = new Date(endDateString);
        if (startDate > endDate) {
          throw new Error("Start date must be less than or equal to end date");
        }
        let daysDifference = Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)
        );
        // if (daysDifference == 0) { daysDifference = 1 }
        return daysDifference + 1;
      } catch (error) {
        console.error(
          "Error Calculating Day Difference:",
          error?.message || error
        );
      }
    };

    return {
      date: {
        startDate,
        endDate,
        getTimeFromDate,
        formatDateString,
        calculateDaysDifference,
      },
    };
  }

  roundOff = (value, decimals = 2) => {
    return Math.round(value);
  };

  areAllValuesSame(arr, value) {
    return arr.every((item) => item === value);
  }

  getMaxDateValueByField = (arr, field) => {
    if (!arr.length) return undefined;

    return arr.reduce((latest, item) => {
      const currentDate = new Date(item[field]).getTime();
      const latestDate = new Date(latest[field]).getTime();
      return currentDate > latestDate ? item : latest;
    })[field];
  };

  convertToSelectFieldValues = (inputArray) => {
    return inputArray.map((item) => {
      // Find the key that contains 'id' for the value
      const valueKey = Object.keys(item).find((key) => key.includes("id"));
      // Find the key that contains 'name' for the label
      const labelKey = Object.keys(item).find((key) => key.includes("name"));

      if (!valueKey || !labelKey) {
        throw new Error(
          "Input objects must contain both an ID field and a Name field"
        );
      }

      return {
        value: item[valueKey],
        label: item[labelKey],
      };
    });
  };

  getUnifiedValueOrLabel = (arr) => {
    if (!arr || arr.length === 0) return "";

    const firstValue = arr[0]?.amount;
    const allSame = arr.every((item) => item?.amount === firstValue);

    return allSame ? String(firstValue) : "Multiple Payments Applied";
  };

  generateInBetweenDates = (start_time, number_of_days, value) => {
    // Parse the start date manually to avoid timezone issues
    if (!start_time || !number_of_days || !value) return [];
    const parseDate = (dateStr) => {
      const [datePart, timePart] = dateStr.split(" ");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes, seconds] = timePart.split(":").map(Number);
      return new Date(year, month - 1, day, hours, minutes, seconds || 0);
    };

    const startDate = parseDate(start_time);

    // Extract time components from start date
    const startHours = startDate.getHours();
    const startMinutes = startDate.getMinutes();
    const startSeconds = startDate.getSeconds();
    // Format date to YYYY-MM-DD HH:MM:SS with leading zeros
    const formatDate = (date) => {
      const pad = (num) => String(num).padStart(2, "0");
      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
          date.getDate()
        )} ` + `${pad(startHours)}:${pad(startMinutes)}:${pad(startSeconds)}`
      );
    };
    const result = [];

    // Add the start date
    result.push({ date: formatDate(startDate), amount: value });

    // Add subsequent days
    for (let i = 1; i < number_of_days; i++) {
      const nextDate = new Date(startDate);
      nextDate.setDate(startDate.getDate() + i);
      result.push({ date: formatDate(nextDate), amount: value });
    }

    return result;
  };

  generateNoonStayDates = (
    start_time,
    endDate,
    checkinGraceHour,
    roomRent
  ) => {
    if (!start_time || !endDate || !roomRent) return [];

    const parseDate = (dateStr) => {
      const [datePart, timePart] = dateStr.replace("T", " ").split(" ");
      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes, seconds] = (timePart || "00:00:00")
        .split(":")
        .map(Number);
      return new Date(year, month - 1, day, hours, minutes, seconds);
    };

    const startDate = parseDate(start_time);
    let endDateObj = parseDate(endDate);

    // Ensure endDate is at 12:00 PM for loop termination logic
    if (endDateObj.getHours() !== 12) {
      endDateObj.setHours(12, 0, 0, 0);
    }

    const checkinGrace = Number(checkinGraceHour || 0);

    // Apply grace hours to check-in time
    // If user enters grace hours, the effective start time moves forward
    const effectiveStartDate = addHours(startDate, checkinGrace);

    // Noon Start Date (Day 1 at 12:00 PM)
    const noonStartDate = new Date(startDate);
    noonStartDate.setHours(12, 0, 0, 0);

    // If initial check-in is after 12 PM, noon start date should be next day 12 PM
    if (startDate.getHours() >= 12) {
      noonStartDate.setDate(noonStartDate.getDate() + 1);
    }

    const result = [];
    const formatDate = (date) => format(date, "yyyy-MM-dd HH:mm:ss");
    const formatLabelDate = (date) => format(date, "dd-MM-yyyy");
    const formatLabelTime = (date) => format(date, "hh:mm a");

    // 1. Grace Period Row
    // Only show if effective start time is BEFORE noon start date
    // And actual start time is also before noon (to handle early check-ins)
    if (
      isBefore(effectiveStartDate, noonStartDate) &&
      startDate.getHours() < 12
    ) {
      const graceHours = differenceInMinutes(noonStartDate, startDate) / 60;
      const hoursDisplay =
        Math.floor(graceHours) === graceHours
          ? graceHours
          : graceHours.toFixed(1);

      result.push({
        date: formatDate(startDate),
        amount: roomRent,
        label: `${formatLabelDate(startDate)} (${hoursDisplay} hr)`,
        isGrace: true,
      });
    }

    // 2. Full Day Rows (Noon to Noon)
    let currentNoon = new Date(noonStartDate);

    // Loop until we reach the end date
    // We compare timestamps to avoid infinite loops
    while (currentNoon.getTime() < endDateObj.getTime()) {
      const nextNoon = new Date(currentNoon);
      nextNoon.setDate(currentNoon.getDate() + 1);

      const label = `${formatLabelDate(currentNoon)} (${formatLabelTime(
        currentNoon
      )} to ${formatLabelDate(nextNoon)} ${formatLabelTime(nextNoon)})`;

      result.push({
        date: formatDate(currentNoon),
        amount: roomRent,
        label: label,
        isGrace: false,
      });

      currentNoon.setDate(currentNoon.getDate() + 1);
    }

    return result;
  };

  applyBoundaryTime = (date, time) => {
    const target = new Date(date);
    const [hours, minutes, seconds] = (time || "12:00:00").split(":").map(Number);
    target.setHours(hours, minutes || 0, seconds || 0, 0);
    return target;
  };

  updateFutureValues(obj, key, newValue) {
    const targetTime = new Date(key).getTime();

    const updatedObj = {};

    for (const dateStr in obj) {
      const currentTime = new Date(dateStr).getTime();

      if (currentTime > targetTime) {
        updatedObj[dateStr] = newValue;
      } else {
        updatedObj[dateStr] = obj[dateStr];
      }
    }

    return updatedObj;
  }
  getNetRetFromBasePrice = (basePrice, stayDays, taxPercentage) => {
    try {
      const modifiedBasePrice = this.truncateNumberByDecimal(basePrice);
      const taxAmount =
        modifiedBasePrice * this.truncateNumberByDecimal(taxPercentage / 100);
      // if ((!modifiedBasePrice, !stayDays, !taxPercentage)) {
      //   throw new Error(
      //     "basePrice or stayDays or taxPercentage is missing to calculate room rent"
      //   );
      // }
      return this.roundOff((modifiedBasePrice + taxAmount) * stayDays);
    } catch (error) {
      throw error;
    }
  };

  getBasePriceFromFomNetRent = (netRent, incOfTax, taxPercentage) => {
    let basePrice;
    if (!incOfTax) {
      basePrice = this.truncateNumberByDecimal(Number(netRent));
    } else {
      basePrice =
        this.truncateNumberByDecimal(Number(netRent)) /
        (1 + this.truncateNumberByDecimal(taxPercentage / 100));
    }

    return this.truncateNumberByDecimal(basePrice);
  };

  getRoomNetRent = (room) => {
    try {
      if (!room) return 0;
      let rent = 0;
      if (room?.day_rents?.length > 0) {
        rent = room.day_rents.reduce((total, day) => {
          if (
            Number(room.is_room_shifted) === 1 &&
            room.actual_checkout_date &&
            this.getLocalTimeFromUtc(day.date)?.dateString >=
              this.getLocalTimeFromUtc(room.actual_checkout_date)?.dateString
          ) {
            return total;
          }
          return (
            total +
            this.getNetRetFromBasePrice(
              day.amount,
              1,
              room.total_occupancy_tax_rate
            )
          );
        }, 0);
      } else {
        const noOfDays = this.getDaysDifferenceByStayType(
          this.getLocalTimeFromUtc(room?.checkin_date)?.dateTimeString,
          this.getLocalTimeFromUtc(
            room.is_room_shifted
              ? room.actual_checkout_date
              : room.expected_checkout_date
          )?.dateTimeString,
          room.stay_type_code,
          room.checkin_grace_hour
        );
        rent = this.getNetRetFromBasePrice(
          room.occupancy_price,
          noOfDays,
          room.total_occupancy_tax_rate
        );
      }

      // --- Extra-person rent (same as the on-screen All-table total) ---
      const extraPersonsRent = (room?.extra_persons || []).reduce(
        (total, person) => {
          const addonPrice = Number(person.addon_price);
          const daysDifference = this.getDaysDifferenceByStayType(
            this.getLocalTimeFromUtc(person?.added_at)?.dateTimeString,
            this.getLocalTimeFromUtc(
              person.actual_checkout_date || person.expected_checkout_date
            )?.dateTimeString,
            room.stay_type_code,
            room.checkin_grace_hour
          );
          const addonTaxRate =
            person.addon_taxes?.length > 0
              ? person.addon_taxes.reduce(
                  (sum, tax) => sum + Number(tax.tax_rate),
                  0
                )
              : Number(room.total_occupancy_tax_rate);
          return (
            total +
            this.roundOff(
              this.getNetRetFromBasePrice(
                addonPrice,
                daysDifference,
                addonTaxRate
              )
            )
          );
        },
        0
      );

      return this.roundOff(Number(rent) + Number(extraPersonsRent));
    } catch (error) {
      return 0;
    }
  };

  truncateNumberByDecimal(value, decimal = 2) {
    return (
      Number((value * 10 ** decimal).toString().split(".")[0]) / 10 ** decimal
    );
  }

  formatDateRange = (start, end) => {
    if (!start || !end) return "N/A";
    const s = this.getLocalTimeFromUtc(start);
    const e = this.getLocalTimeFromUtc(end);
    if (!s || !e) return "N/A";
    return `${s.date} - ${e.date}`;
  };

  formatDate = (date) => {
    return date.toISOString().slice(0, 19).replace("T", " ") + ".000Z";
  };

  formatDateForCSV = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return `"${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}"`;
  };

  getDaysOfYear(year) {
    // Create an array of all days in the year
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);
    const allDaysOfYear = eachDayOfInterval({
      start: startOfYear,
      end: endOfYear,
    });

    // Format each date in the array
    const formattedDaysOfYear = allDaysOfYear.map((date) =>
      format(date, "EEE dd MMM")
    );

    return formattedDaysOfYear;
  }

  isValidDate(inputDate) {
    if (!inputDate) return false; // Handle cases where inputDate is null or undefined
    const isValidDate = moment(inputDate, true).isValid();
    if (!isValidDate) return false; // If date is not valid, return false
    const year = moment(inputDate).year();
    const isValidYear = /^\d{1,4}$/.test(year.toString()); // Check if year has 1 to 4 digits
    return isValidYear; // Return whether year is valid or not
  }

  formatDateTimeToDate = (date) => {
    return date ? new Date(date).toISOString().split("T")[0] : null;
  };

  formatDateToDateTime = (dateStr) => {
    return dateStr ? new Date(dateStr).toISOString() : null;
  };

  toDateTimeLocalInputValue = (value) => {
    if (!value) return undefined;
    if (typeof value === "string") return value.replace(" ", "T").slice(0, 16);
    const date = value?.toDate ? value.toDate() : new Date(value);
    return isNaN(date.getTime()) ? undefined : format(date, "yyyy-MM-dd'T'HH:mm");
  };

  getSumPreviousAmount = (payments, targetPaymentId) => {
    try {
      let sum = 0;
      for (const payment of payments) {
        if (payment.payment_id < targetPaymentId) {
          sum += payment.paid_amount;
        } else if (payment.payment_id === targetPaymentId) {
          break;
        }
      }
      return sum;
    } catch (error) {
      console.error("Error getting sum of previous amount:", error?.message);
    }
  };

  viewPaymentReceipt = (billData) => {
    let printableTemplate = this.generateReceiptTemplate(billData);
    const printWindow = window.open("", "Hotel_Receipt");
    printWindow.document.body.innerHTML = printableTemplate;
  };

  printPaymentReceipt = (billData) => {
    if (billData?.billHeader === "ADVANCE RECEIPT") {
      billData.previousPaidAmount = this.getSumPreviousAmount(
        billData?.paymentData,
        billData?.paymentDetail?.payment_id
      );
    }
    let printableTemplate = this.generatePrintTemplate(billData);

    const printWindow = window.open("", "Print Bill");
    printWindow.document.write(printableTemplate);
    const img = printWindow.document.getElementById("hotel-logo-image");

    img.onload = () => {
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  SUPPORTED_IMAGE_FORMATS = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  SUPPORTED_ATTACHMENTS_FORMATS = [
    ...this.SUPPORTED_IMAGE_FORMATS,
    "application/pdf",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  generateReceiptTemplate = (data) => {
    const t = i18next.t;
    const template = `
    <!DOCTYPE html>
    <html lang="en">

    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta http-equiv="X-UA-Compatible" content="ie=edge" />
      <link rel="icon" href="./favicon.ico" type="image/x-icon" />
      <style>
         @media print {
            .received-amount div {
                background-color: #f0f0f0 !important;
                -webkit-print-color-adjust: exact; /* For Safari and Chrome */
                print-color-adjust: exact; /* For Firefox */
            }
        }

        .dotted-underline {          
          border-bottom: 1px dotted black;
        }

        body {
          font-family: Arial, sans-serif;
          background-color: #ffffff;
        }

        /* CSS Variables for Colors */
        :root {
          --primary-color: #3a3a3a;
          --secondary-color: #3a3a3a;
          --tertiary-color: #858585;
          --border-color: #d7d7d7;
          --background-color: #F3F3F3;
        }

        .invoice-table {
          width: 100%;
          border: 1px solid #e9e9e9;
          border-collapse: collapse;
          font-size: 12px;
          margin-top: 5%;
        }

        .invoice-table th,
        .invoice-table td {
          border: 1px solid #e9e9e9;
          padding: 5px;
          text-align: left;
        }

        .invoice-table th {
          background-color: var(--primary-color);
          color: #ffffff;
          font-weight: 500;
        }
      </style>
    </head>

    <body>
      <div style="height: 97.2vh; width: 90%; margin: 0 auto;">
        <div style="
              width: 90%;
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin: 1% auto 0 auto;
            ">
          <div style="width: 65%">
            <img data-testid='receipt-hotel-logo' id="hotel-logo-image" src=${data?.userData?.hotel_logo
      } alt="" height="70px" width="70px"
              style="border-radius: 3px" />
            <p data-testid ='receipt-hotel-name' style="font-size: 16px; font-weight: 700; color: var(--primary-color)">
              ${data?.userData?.hotel_name}
            </p>
            <p data-testid ='receipt-hotel-address' style="color: #3a3a3a; font-size: 12px; font-weight: 400; max-width: 200px; line-height: 1.5;">
              ${data?.userData?.hotel_address}
            </p>
            <div>
              <p data-testid ='receipt-hotel-contact-number' style="color: #3a3a3a; font-size: 12px; font-weight: 400; margin: 0">
                ${data?.userData?.hotel_contact_number}
              </p>
            </div>
            <div data-testid ='receipt-hotel-gstin-number' style="color: #3a3a3a; font-size: 12px; font-weight: 400">
              <p>${t("pages.receipt_page.GSTIN")} : ${data?.userData?.hotel_gstin_number
        ? data?.userData.hotel_gstin_number
        : "-"
      }</p>
            </div>
          </div>
          <div style="width: 35%;">
            <p data-testid ='receipt-hotel-payment-receipt' style="font-size: 20px; font-weight: 500; color: var(--primary-color);margin:0;">
              ${t("pages.receipt_page.payment_receipt")}
            </p>
            <p data-testid ='receipt-hotel-receipt-number-label' style="font-size: 12px; font-weight: 500">
               ${t("pages.receipt_page.receipt_number")} : ${data?.bookingData?.receipt_number || "-"
      }
            </p>
            <p data-testid ='receipt-hotel-payment-date-label' style="font-size: 12px; font-weight: 500">
              ${t("pages.receipt_page.payment_date")} : <span>${this.getLocalTimeFromUtc(data?.bookingData?.payment_date)?.ddmmyyyy
      }</span>
            </p>
          </div>
        </div>

        <div style="width: 90%; margin: 0 auto">
          <hr style="border: 1px solid #d7d7d7" />
          <div class="parent-body" style="display: flex; flex-direction: column; padding-top: 16px;">
            <div class="billto-details" style="display: flex; flex-direction: column; gap: 16px;">
              <div class="customer-name" style="display: flex; justify-content: flex-start; align-items: center;">
                <p style="width: 40%; font-size: 12px; font-weight: 400px; margin: 0;">${t(
        "pages.receipt_page.customer_name"
      )}</p>
                <p class="dotted-underline"
                  style="width: 50%; font-size: 12px; font-weight: 700; margin: 0; color: var(--primary-color);">
                  ${data?.bookingData.name?.replace(/\b\w/g, (char) =>
        char.toUpperCase()
      )}</p>
              </div>
              <div class="amount-received-words" style="display: flex; justify-content: flex-start; align-items: center;">
                <p style="width: 40%; font-size: 12px; font-weight: 400px; margin: 0">${t(
        "pages.receipt_page.amount_received_in_words"
      )}</p>
                <p class="dotted-underline"
                  style="width: 50%; font-size: 12px; font-weight: 700; margin: 0; color: var(--primary-color);">
                  ${!isNaN(data.bookingData?.paid_amount)
        ? numWords(data.bookingData?.paid_amount).replace(
          /\b\w/g,
          (char) => char.toUpperCase()
        ) + " Only"
        : "-"
      }
                </p>
              </div>

              <div class="amount-received-words" style="display: flex; justify-content: flex-start; align-items: center;">
                <p style="width: 40%; font-size: 12px; font-weight: 400px; margin: 0">${t(
        "pages.receipt_page.payment_mode"
      )}</p>
                <p class="dotted-underline"
                  style="width: 50%; font-size: 12px; font-weight: 700; margin: 0; color: var(--primary-color);">
                  ${data?.bookingData.payment_type}</p>
              </div>

              <div class="customer-value" style="width: 50%;">
              </div>
            </div>

            <div class="received-amount" style="display: flex; justify-content: flex-end; padding-top: 12px;">
              <div
                style="background-color: #f0f0f0;border-radius: 4px; width: 224px; height: 36px;display: flex; text-align: center;justify-content: center;align-items: center;">
                <p style="font-size: 14px;">${t(
        "pages.receipt_page.received_amount"
      )} <span style="font-size: 14px; font-weight: 700; color: var(--primary-color);">₹${data.bookingData?.paid_amount || 0
      } <span> </p>
              </div>
            </div>

            <div class="signatures" style="display: flex; justify-content:space-between; padding-top: 40px;">
              <p style="font-size: 12px; font-weight: 400;">${t(
        "pages.receipt_page.customer_signature"
      )}</p>
              <p style="font-size: 12px; font-weight: 400;">${t(
        "pages.receipt_page.cashier_signature"
      )}</p>
            </div>

          </div>
        </div>
      </div>
    </body>
    </html>`;

    return template;
  };

  getBillingCategories = (invoiceDataArray) => {
    const categories = {
      stay: false,
      charges_posting: false,
      paid_out: false,
      guest_disbursement: false,
    };
    const invoices = Array.isArray(invoiceDataArray)
      ? invoiceDataArray
      : [invoiceDataArray];
    for (const invoice of invoices) {
      if (!invoice?.invoice_items) continue;
      const seen = new Set();
      for (const item of invoice.invoice_items) {
        if (
          item.invoice_item_price > 0 ||
          item.invoice_item_addons?.length > 0
        ) {
          categories.stay = true;
        }
        const occId =
          item?.occupancy_id?.occupancy_id ?? item?.occupancy_id;
        if (seen.has(occId)) continue;
        seen.add(occId);
        const chargePostings = item?.occupancy_id?.chargeposting || [];
        if (chargePostings.length > 0) categories.charges_posting = true;
        const charges = item?.occupancy_id?.occupancycharge || [];
        for (const charge of charges) {
          if (charge.charge_type === "PAID_OUT") categories.paid_out = true;
          if (charge.charge_type === "GUEST_DISBURSEMENT")
            categories.guest_disbursement = true;
        }
      }
    }
    return categories;
  };

  generateInvoiceTemplate = (data, invoiceData) => {
    const t = i18next.t;
    try {
      let grossTotal = 0;
      const getSubtotal = (invoiceTotalPrice, invoiceTaxes, isGrossAccum) => {
        let invoiceItemTotalPrice = new Decimal(invoiceTotalPrice || 0);
        let invoiceTaxesTotal = (invoiceTaxes || []).reduce(
          (sum, tax) =>
            sum.plus(
              new Decimal(tax?.invoice_tax_amount || tax?.addon_tax_amount || 0)
            ),
          new Decimal(0)
        );
        let subtotal = invoiceItemTotalPrice.plus(invoiceTaxesTotal);

        const rounded = Number(subtotal).toFixed(2);

        if (isGrossAccum) {
          grossTotal += Number(rounded);
        }

        return rounded;
      };

      const {
        invoice_number,
        n_bill_number,
        customer_name,
        invoice_date,
        invoice_name,
        invoice_mobile,
        invoice_address,
        invoice_items,
        gstin_number,
        supplier_gstin_number,
        supplier_hsn_sac,
        total_amount,
        round_off_value,
        round_off_amount,
        pricing_rule_id,
      } = invoiceData || {};

      const {
        hotel_logo,
        hotel_name,
        hotel_address,
        hotel_email,
        hotel_contact_number,
      } = data?.userData || {};
      const template = `
      <!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta http-equiv="X-UA-Compatible" content="ie=edge" />
    <link rel="icon" href="./favicon.ico" type="image/x-icon" />
    <style>
      /* CSS Reset */
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      
      /* CSS Variables for Colors */
      :root {
        --primary-color: #3a3a3a;
        --secondary-color: #3a3a3a;
        --tertiary-color: #858585;
        --border-color: #d7d7d7;
        --background-color: #F3F3F3;
      }

      body {
        font-family: Arial, sans-serif;
        background-color: #ffffff !important;
      }

      .container {
        height: 97.2vh;
        width: 90%;
        margin: 1% auto 0 auto;
      }

      .header {
        display: flex;
        justify-content: space-between;
      }

      .hotel-info {
        width: 65%;
      }

      .hotel-logo {
        height: 70px;
        width: 70px;
        border-radius: 3px;
      }

      .hotel-name {
        font-size: 16px;
        font-weight: 700;
        color: var(--primary-color);
        margin: 2% 0;
      }

      .hotel-address {
        color: var(--secondary-color);
        font-size: 12px;
        font-weight: 400;
        max-width: 230px;
        line-height: 1.5;
      }

      .contact-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 3px;
      }

      .contact-item {
        display: flex;
        align-items: center;
        padding: 0;
        width: 30%;
        justify-content: flex-start;
        margin-top: 1%;
      }

      .contact {
        margin-top : 3%; 
        color: var(--secondary-color);
        font-size: 12px;
        font-weight: 400;
      }

      .contact tr td {
        padding-top: 2%;
        padding-bottom: 2%;
      }

      .contact-label {
        color: var(--tertiary-color);
        font-size: 12px;
        font-weight: 400;
        font-size: 12px;
        margin: 0;
      }

      .contact-value {
        color: var(--secondary-color);
        font-size: 12px;
        font-weight: 400;
        margin: 0;
      }

      .bill-info {
        width: 35%;
      }
      
      .bill-name {
        color: var(--secondary-color);
        font-size: 12px;
        font-weight: 400;
        max-width: 230px;
        margin : 1% 0 1% 0;
      }

      .bill-address {
        color: var(--secondary-color);
        font-size: 12px;
        font-weight: 400;
        max-width: 230px;
        line-height: 1.5;
      }

      .bill-header {
        font-size: 28px;
        font-weight: 500;
        color: var(--primary-color);
      }

      .invoice-info {
        font-size: 12px;
        font-weight: 500;
      }

      .invoice-info tr td {
        padding-top: 2%;
        padding-bottom: 2%;
      }

      .divider {
        border: 1px solid var(--border-color);
        margin: 1% 0
      }

      .bill-to {
        font-size: 14px;
        font-weight: 700;
        color: var(--primary-color);
        padding-top: 1%;
        margin-bottom: 1%;
      }

      .table-container {
        display: flex;
        justify-content: right;
        width: 98%;
      }

      .invoice-table {
        width: 100%;
        border: 1px solid #e9e9e9;
        border-collapse: collapse;
        font-size: 12px;
        margin-top: 5%;
      }

      .invoice-table th,
      .invoice-table td {
        border: 1px solid #e9e9e9;
        padding: 5px;
        text-align: left;
        white-space: nowrap;
      }

      .invoice-table td.desc-cell {
        white-space: normal;
      }

      .invoice-table th {
        background-color: var(--primary-color) !important;
        color: #ffffff;
        font-weight: 500;
      }

      .subtotal-row {
        text-align: right;
      }

      .summary-table {
        margin-top: 3%;
        background-color:  #f0f0f0 !important;
        font-size: 12px;
        padding: 10px;
        border-radius: 4px;
      }

       @media print {
            .invoice-table th {
                background-color: var(--primary-color) !important;
                -webkit-print-color-adjust: exact; /* For Safari and Chrome */
                print-color-adjust: exact; /* For Firefox */
            }
            .summary-table {
                background-color:  #f0f0f0 !important;
                -webkit-print-color-adjust: exact; /* For Safari and Chrome */
                print-color-adjust: exact; /* For Firefox */
            }
        }
      .footer {
        margin-top: 20%;
      }

      .footer-text {
        text-align: center;
        margin-bottom: 10%;
        font-size: 12px;
        font-weight: 400;
        color: var(--primary-color);
      }

      .signature-section {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 12px;
        color: #252525;
      }

      .child-row td {
    padding-left: 20px;  /* Indent child rows */
}

       
    </style>
  </head>

  <body>
    <div class="container">
      <div class="header">
        <div class="hotel-info">
          <img id="hotel-logo-image" class="hotel-logo" src=${hotel_logo} alt="" />
          <p class="hotel-name">${hotel_name}</p>
          ${hotel_address?.trim() && hotel_address !== "null"
          ? `<p class="hotel-address">${hotel_address}</p>`
          : ""
        }
        <table class="contact">
          <tbody>
            <tr>
              <td>${t("pages.invoice_page.contact")}</td>
              <td>: ${hotel_contact_number}</td>
            </tr>
            ${hotel_email &&
          hotel_email !== "null" &&
          hotel_email !== null &&
          hotel_email !== undefined &&
          hotel_email.trim() !== ""
          ? `<tr>
                     <td>${t("pages.invoice_page.email")}</td>
                     <td>: ${hotel_email}</td>
                   </tr>`
          : ""
        }
            
            <tr>
              <td>${t("pages.invoice_page.GSTIN")}</p>
              <td>: ${supplier_gstin_number ? supplier_gstin_number : "-"}</td>
            </tr>
          </tbody>
          </table>
        </div>
        <div class="bill-info">
         <p class="bill-header">${t(
          `pages.invoice_page.${data?.billHeader
            ?.toLowerCase()
            .replace(/\s+/g, "_")}`
        )}</p>
          <table class="invoice-info">
          <tbody>
          <tr>
          <td>${t("pages.invoice_page.invoice_number")}</td>
          <td>: ${n_bill_number || invoice_number || "-"}</td>
          </tr>
          <tr>
          <td>
            ${t("pages.invoice_page.invoice_date")}
          </td> 
          <td>
            : ${this.getLocalTimeFromUtc(
          invoice_date?.replace("T", " ").split(".")[0]
        ).ddmmyyyy
        }
          </td>
          </tr>
          </tbody>
          </table>
        </div>
      </div>
      <hr class="divider" />

      <div class="bill-to-section">
     <p class="bill-to">${t("pages.invoice_page.bill_to")}</p>
     
     ${invoice_name && customer_name &&
          String(invoice_name).trim().toLowerCase() !== String(customer_name).trim().toLowerCase()
          ? `<p class="bill-name">${customer_name}</p>  <p class="bill-name">${invoice_name}</p>`
          : `<p class="bill-name">${invoice_name || customer_name}</p>`
        }
        <p class="bill-address">${invoice_address}</p>
        <div class="contact-info">
          <div class="contact-item">
            <p class="contact-label">${t(
          "pages.invoice_page.contact"
        )}&nbsp;</p>
            <p class="contact-value"> : ${invoice_mobile}</p>
          </div>
          ${!!gstin_number
          ? `<div class="contact-item">
            <p class="contact-label">${t(
            "pages.invoice_page.GSTIN"
          )}&nbsp;</p><p class="contact-value"> : ${gstin_number}</p>
          </div>`
          : ""
        }
        </div>
      </div>

      ${(() => {
        const allGroupTaxRates = [];
        for (const r of (invoice_items || [])) {
          for (const tx of (r?.invoice_taxes || [])) allGroupTaxRates.push(parseFloat(tx?.tax_rate));
          for (const ad of (r?.invoice_item_addons || [])) {
            for (const tx of (ad?.invoice_item_addon_taxes || [])) allGroupTaxRates.push(parseFloat(tx?.addon_tax_rate));
          }
        }
        const groupUniformRate = allGroupTaxRates.length > 0 && allGroupTaxRates.every((r) => r === allGroupTaxRates[0]) ? allGroupTaxRates[0] : null;
        return `<div>
        <table class="invoice-table">
          <thead>
            <tr>
               <th style="text-align: center;">${t("pages.invoice_page.s_no")}</th>
              <th>${t("Room No")}</th>
              <th>${t("pages.invoice_page.description")}</th>
              <th>${t("pages.invoice_page.hsn_sac")}</th>
              <th style="text-align: right;">${t("pages.invoice_page.base_price")}</th>
              <th style="text-align: center;">${t("pages.invoice_page.days")}</th>
              <th style="text-align: right;">${t("pages.invoice_page.total_base_price")}</th>
              <th style="text-align: right;">${t("pages.invoice_page.sgst")}${groupUniformRate !== null ? "<br/>" + (groupUniformRate / 2).toFixed(2) + "%" : ""}</th>
              <th style="text-align: right;">${t("pages.invoice_page.cgst")}${groupUniformRate !== null ? "<br/>" + (groupUniformRate / 2).toFixed(2) + "%" : ""}</th>
              <th style="text-align: right;">${t("pages.invoice_page.net_rent")}</th>
            </tr>
          </thead>
          <tbody>
            ${invoice_items
          ?.map((room, ind) => {
            const occId =
              room?.occupancy_id?.occupancy_id ?? room?.occupancy_id;
            const roomNo = room?.room_id?.room_no || room?.occupancy_id?.room_id?.room_no || "-";
            const isFirstForOccupancy =
              invoice_items.findIndex(
                (it) =>
                  (it?.occupancy_id?.occupancy_id ?? it?.occupancy_id) === occId
              ) === ind;
            const occupancyChargePostings = isFirstForOccupancy
              ? room?.occupancy_id?.chargeposting || []
              : [];

            const occupancyAddons = room?.occupancy_id?.addons || [];
            const extraPersonsRows =
              room?.invoice_item_addons
                ?.map((per, perInd) => {
                  const addonName = occupancyAddons[perInd]?.addon_name || "";
                  const epLabel = addonName ? `${addonName} - ${t("pages.invoice_page.extra_persons")}` : `${t("pages.invoice_page.extra_persons")} ${perInd + 1}`;
                  return `
                <tr>
                    <td></td>
                    <td>${roomNo}</td>
                    <td>${epLabel}</td>
                    <td></td>
                    <td style="text-align: right;">₹ ${parseFloat(per.addon_price).toFixed(2)}</td>
                    <td style="text-align: center;">${per?.stay_days}</td>
                    <td style="text-align: right;">₹ ${parseFloat(
                    per.addon_price * per?.stay_days
                  ).toFixed(2)}</td>
                    ${per?.invoice_item_addon_taxes
                      ?.map((tax) => {
                        return `<td style="text-align: right;">₹ ${Number(tax?.addon_tax_amount).toFixed(
                          2
                        )}${groupUniformRate === null ? "<br/>" + tax?.addon_tax_rate + "%" : ""}</td>`;
                      })
                      .join("")}
                    <td style="text-align: right;">₹ ${getSubtotal(
                        per?.addon_total_price,
                        per?.invoice_item_addon_taxes,
                        true
                      )}</td>
                </tr>`;
                })
                .join("") || "";

            const chargePostingRows =
              occupancyChargePostings
                ?.map((cp, cpInd) => {
                  const cpTotal = parseFloat(cp?.total || 0);
                  const cpRawAmount = parseFloat(cp?.amount || 0);
                  const cpTaxPercent = parseFloat(cp?.tax_percent || 0);
                  let cpAmount, cpTaxAmount;
                  if (cp?.incl_tax && cpTaxPercent > 0) {
                    cpAmount = parseFloat((cpTotal / (1 + cpTaxPercent / 100)).toFixed(2));
                    cpTaxAmount = parseFloat((cpTotal - cpAmount).toFixed(2));
                  } else {
                    cpAmount = cpRawAmount;
                    cpTaxAmount = cpTotal - cpRawAmount;
                  }
                  const cpSgst = cpTaxAmount > 0 ? (cpTaxAmount / 2).toFixed(2) : "0.00";
                  const cpCgst = cpTaxAmount > 0 ? (cpTaxAmount / 2).toFixed(2) : "0.00";
                  grossTotal += cpTotal;
                  return `
            <tr>
              <td style="text-align: center;">${cpInd + 1}</td>
              <td>${roomNo}</td>
              <td class="desc-cell">${[cp?.outlet?.outlet_name, cp?.description].filter(v => v && v.trim() && v.trim() !== "-" && v.trim() !== "- -").join(" - ") || "-"}</td>
              <td></td>
              <td style="text-align: right;">₹ ${cpAmount.toFixed(2)}</td>
              <td style="text-align: center;">-</td>
              <td style="text-align: right;">₹ ${cpAmount.toFixed(2)}</td>
              <td style="text-align: right;">₹ ${cpSgst}${groupUniformRate === null && cp?.incl_tax && cp?.tax_percent ? "<br/>" + (parseFloat(cp.tax_percent) / 2).toFixed(2) + "%" : ""}</td>
              <td style="text-align: right;">₹ ${cpCgst}${groupUniformRate === null && cp?.incl_tax && cp?.tax_percent ? "<br/>" + (parseFloat(cp.tax_percent) / 2).toFixed(2) + "%" : ""}</td>
              <td style="text-align: right;">₹ ${cpTotal.toFixed(2)}</td>
            </tr>`;
                })
                .join("") || "";

            const charges = isFirstForOccupancy
              ? room?.occupancy_id?.occupancycharge || []
              : [];
            const guestDisbursement = charges.filter(
              (c) => c.charge_type === "GUEST_DISBURSEMENT"
            );
            const paidOut = charges.filter(
              (c) => c.charge_type === "PAID_OUT"
            );

            const guestDisbursementRows =
              guestDisbursement
                ?.map((gd, gdInd) => {
                  const gdAmount = parseFloat(gd?.amount || 0);
                  grossTotal += gdAmount;
                  return `
            <tr>
              <td style="text-align: center;">${gdInd + 1}</td>
              <td>${roomNo}</td>
              <td>${gd?.disbursement_type?.disbursement_type_name || "-"}</td>
              <td></td>
              <td colspan="4"></td>
              <td style="text-align: right;" colspan="2">₹ ${gdAmount.toFixed(2)}</td>
            </tr>`;
                })
                .join("") || "";

            const paidOutRows =
              paidOut
                ?.map((po, poInd) => {
                  const poAmount = parseFloat(po?.amount || 0);
                  grossTotal -= poAmount;
                  return `
            <tr>
              <td style="text-align: center;">${poInd + 1}</td>
              <td>${roomNo}</td>
              <td>${po?.paid_out_type?.paid_out_type_name || "-"}</td>
              <td></td>
              <td colspan="4"></td>
              <td style="text-align: right;" colspan="2">- ₹ ${poAmount.toFixed(2)}</td>
            </tr>`;
                })
                .join("") || "";

            return `
            <tr>
                <td style="text-align: center;">${ind + 1}</td>
                <td>${roomNo}</td>
                <td class="desc-cell">${room?.room_id?.room_type_id?.room_type_name
              }<br />
                ${format(
                this.getLocalTimeFromUtc(
                  room.checkin_date?.replace("T", " ").split(".")[0]
                ).dateTimeString,
                "dd-MM-yyyy hh:mm a"
              )}<br />
                ${format(
                this.getLocalTimeFromUtc(
                  room.checkout_date?.replace("T", " ").split(".")[0]
                ).dateTimeString,
                "dd-MM-yyyy hh:mm a"
              )}
                 ${Number(room.discount_amount) > 0
                ? `<br/><span style="color: #d32f2f;">${t("Discount")}: -₹ ${parseFloat(room.discount_amount).toFixed(2)}</span>`
                : ""
              }
                </td>

                <td>${supplier_hsn_sac}</td>

                <td style="text-align: right;">₹ ${parseFloat(room.invoice_item_price).toFixed(2)}</td>

                <td style="text-align: center;">${room?.stay_days}</td>
              <td style="text-align: right;">₹ ${parseFloat(
                room.invoice_item_price * room?.stay_days
              ).toFixed(2)}</td>

                ${room?.invoice_taxes
                ?.map((tax) => {
                  return `<td style="text-align: right;">₹ ${Number(tax?.invoice_tax_amount).toFixed(2)}${groupUniformRate === null ? "<br/>" + parseFloat(tax?.tax_rate).toFixed(2) + "%" : ""}</td>`;
                })
                .join("")}
                <td style="text-align: right;">₹ ${getSubtotal(
                  room?.invoice_item_total_price,
                  room?.invoice_taxes,
                  true
                )}</td>
              </tr>
              ${extraPersonsRows}
              ${chargePostingRows}
              ${guestDisbursementRows}
              ${paidOutRows}`;
          })
          .join("")}`;
      })()}
             
              ${(pricing_rule_id?.rule_name || invoiceData?.coupon_code || Number(invoiceData?.coupon_discount_amount) > 0)
          ? `<tr>
                <td colspan="8"></td>
                <td style="color: #252525;">${t("Gross Total")}</td>
                <td style="color: #3D3D3D; font-weight:600;">₹ ${grossTotal.toFixed(2)}</td>
              </tr>
              <tr>
                <td colspan="8"></td>
                <td style="color: #252525;">${t("Discount")} ${invoiceData?.coupon_code ? `(${invoiceData.coupon_code})` : ""}</td>
                <td style="color: #d32f2f; font-weight:600;">-₹ ${Number(invoiceData?.coupon_discount_amount) > 0
            ? Math.min(
              Math.max(0, parseFloat(invoiceData.coupon_discount_amount) || 0),
              Math.max(0, grossTotal)
            ).toFixed(2)
            : Math.max(0, grossTotal - Math.max(0, parseFloat(total_amount) || 0)).toFixed(2)}</td>
              </tr>`
          : ""
        }
         <tr>
                <td colspan="8"></td>
                <td style="color: #252525;">${t(
          "pages.invoice_page.subtotal"
        )}</td>
                <td style="color: #3D3D3D; font-weight:600;">₹ ${Math.max(
          0,
          parseFloat(total_amount) || 0
        ).toFixed(2)}</td>
              </tr>
          </tbody>
        </table>

        <div class="table-container">
          <table class="summary-table">
            <tbody>
        ${Math.abs(parseFloat(round_off_value)) > 0
          ? `<tr>
          <td>${t("pages.invoice_page.rounding_off")}</td>
          <td style="padding-left: 5px;color: var(--primary-color); text-align: right; font-weight: 600">${Math.abs(
            parseFloat(round_off_value)
          )}</td>
        </tr>`
          : ""
        }
              <tr>
                <td style="padding-right: 10px;">${t(
          "pages.invoice_page.total"
        )} </td>
                <td style="padding-left: 10px;color: var(--primary-color); text-align: right; font-weight: 600">₹ ${this.roundOff(round_off_amount) || 0
        }</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      ${invoice_items.length > 8 ||
          (invoice_items.length > 3 && invoice_items.length <= 4)
          ? '<div style="page-break-after: always;"></div>'
          : ""
        }  
      <div class="footer">
        <div class="footer-text">
          <p>
          ${t(
          "pages.invoice_page.regardless_of_the_billing_instruction_you_are_held_personally_liable_for_the_total_amount_of_this_bill"
        )}
          </p>
        </div>

        <div class="signature-section">
          <p>${t("pages.invoice_page.customer_signature")}</p>
          <p>${t("pages.invoice_page.cashier_signature")}</p>
        </div>
      </div>
    </div>
  </body>
</html>
    `;

      return template;
    } catch (error) {
      console.error("error in generate invoice template:", error);
    }
  };

  _buildInvoicePageShell = (data, invoiceData, _unused, bodyContent) => {
    const t = i18next.t;
    const {
      invoice_number,
      n_bill_number,
      customer_name,
      invoice_date,
      invoice_name,
      invoice_mobile,
      invoice_address,
      gstin_number,
      supplier_gstin_number,
    } = invoiceData || {};
    const {
      hotel_logo,
      hotel_name,
      hotel_address,
      hotel_email,
      hotel_contact_number,
    } = data?.userData || {};

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  :root { --primary-color: #3a3a3a; --secondary-color: #3a3a3a; --tertiary-color: #858585; --border-color: #d7d7d7; }
  body { font-family: Arial, sans-serif; background-color: #ffffff !important; }
  .container { width: 90%; margin: 1% auto 0 auto; }
  .header { display: flex; justify-content: space-between; }
  .hotel-info { width: 65%; }
  .hotel-logo { height: 70px; width: 70px; border-radius: 3px; }
  .hotel-name { font-size: 16px; font-weight: 700; color: var(--primary-color); margin: 2% 0; }
  .hotel-address { color: var(--secondary-color); font-size: 12px; font-weight: 400; max-width: 230px; line-height: 1.5; }
  .contact { margin-top: 3%; color: var(--secondary-color); font-size: 12px; font-weight: 400; }
  .contact tr td { padding-top: 2%; padding-bottom: 2%; }
  .bill-info { width: 35%; }
  .bill-header { font-size: 28px; font-weight: 500; color: var(--primary-color); }
  .invoice-info { font-size: 12px; font-weight: 500; }
  .invoice-info tr td { padding-top: 2%; padding-bottom: 2%; }
  .divider { border: 1px solid var(--border-color); margin: 1% 0; }
  .bill-to { font-size: 14px; font-weight: 700; color: var(--primary-color); padding-top: 1%; margin-bottom: 1%; }
  .bill-name { color: var(--secondary-color); font-size: 12px; font-weight: 400; max-width: 230px; margin: 1% 0; }
  .bill-address { color: var(--secondary-color); font-size: 12px; font-weight: 400; max-width: 230px; line-height: 1.5; }
  .contact-info { display: flex; flex-direction: column; align-items: flex-start; gap: 3px; }
  .contact-item { display: flex; align-items: center; padding: 0; width: 30%; justify-content: flex-start; margin-top: 1%; }
  .contact-label { color: var(--tertiary-color); font-size: 12px; margin: 0; }
  .contact-value { color: var(--secondary-color); font-size: 12px; margin: 0; }
  .table-container { display: flex; justify-content: right; width: 98%; }
  .invoice-table { width: 100%; border: 1px solid #e9e9e9; border-collapse: collapse; font-size: 12px; margin-top: 3%; }
  .invoice-table th, .invoice-table td { border: 1px solid #e9e9e9; padding: 5px; text-align: left; white-space: nowrap; }
  .invoice-table td.desc-cell { white-space: normal; }
  .invoice-table th { background-color: var(--primary-color) !important; color: #ffffff; font-weight: 500; }
  .summary-table { margin-top: 3%; background-color: #f0f0f0 !important; font-size: 12px; padding: 10px; border-radius: 4px; }
  @media print {
    .invoice-table th { background-color: var(--primary-color) !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .summary-table { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
  .footer { margin-top: 15%; }
  .footer-text { text-align: center; margin-bottom: 10%; font-size: 12px; color: var(--primary-color); }
  .signature-section { display: flex; align-items: center; justify-content: space-between; font-size: 12px; color: #252525; }
</style>
</head>
<body>
<div class="container">
  <div class="header">
    <div class="hotel-info">
      <img class="hotel-logo" src=${hotel_logo} alt="" />
      <p class="hotel-name">${hotel_name}</p>
      ${hotel_address?.trim() && hotel_address !== "null" ? `<p class="hotel-address">${hotel_address}</p>` : ""}
      <table class="contact"><tbody>
        <tr><td>${t("pages.invoice_page.contact")}</td><td>: ${hotel_contact_number}</td></tr>
        ${hotel_email && hotel_email !== "null" && hotel_email?.trim() !== "" ? `<tr><td>${t("pages.invoice_page.email")}</td><td>: ${hotel_email}</td></tr>` : ""}
        <tr><td>${t("pages.invoice_page.GSTIN")}</td><td>: ${supplier_gstin_number || "-"}</td></tr>
      </tbody></table>
    </div>
    <div class="bill-info">
      <p class="bill-header">${t("pages.invoice_page.tax_invoice")}</p>
      <table class="invoice-info"><tbody>
        <tr><td>${t("pages.invoice_page.invoice_number")}</td><td>: ${n_bill_number || invoice_number || "-"}</td></tr>
        <tr><td>${t("pages.invoice_page.invoice_date")}</td><td>: ${this.getLocalTimeFromUtc(invoice_date?.replace("T", " ").split(".")[0]).ddmmyyyy}</td></tr>
      </tbody></table>
    </div>
  </div>
  <hr class="divider" />
  <div class="bill-to-section">
    <p class="bill-to">${t("pages.invoice_page.bill_to")}</p>
    ${invoice_name && customer_name && String(invoice_name).trim().toLowerCase() !== String(customer_name).trim().toLowerCase()
      ? `<p class="bill-name">${customer_name}</p><p class="bill-name">${invoice_name}</p>`
      : `<p class="bill-name">${invoice_name || customer_name}</p>`}
    <p class="bill-address">${invoice_address}</p>
    <div class="contact-info">
      <div class="contact-item"><p class="contact-label">${t("pages.invoice_page.contact")}&nbsp;</p><p class="contact-value"> : ${invoice_mobile}</p></div>
      ${gstin_number ? `<div class="contact-item"><p class="contact-label">${t("pages.invoice_page.GSTIN")}&nbsp;</p><p class="contact-value"> : ${gstin_number}</p></div>` : ""}
    </div>
  </div>
  ${bodyContent}
  <div class="footer">
    <div class="footer-text"><p>${t("pages.invoice_page.regardless_of_the_billing_instruction_you_are_held_personally_liable_for_the_total_amount_of_this_bill")}</p></div>
    <div class="signature-section">
      <p>${t("pages.invoice_page.customer_signature")}</p>
      <p>${t("pages.invoice_page.cashier_signature")}</p>
    </div>
  </div>
</div>
</body>
</html>`;
  };

  generateSeparateInvoiceTemplates = (data, invoiceData) => {
    const t = i18next.t;
    const templates = [];
    const {
      invoice_items,
      supplier_hsn_sac,
    } = invoiceData || {};
    if (!invoice_items?.length) return templates;

    const Decimal_ = require("decimal.js");

    const allChargePostings = [];
    const allGuestDisbursements = [];
    const allPaidOuts = [];
    const seenOccupancies = new Set();

    for (const item of invoice_items) {
      const occId = item?.occupancy_id?.occupancy_id ?? item?.occupancy_id;
      const roomNo = item?.room_id?.room_no || item?.occupancy_id?.room_id?.room_no || "-";
      if (!seenOccupancies.has(occId)) {
        seenOccupancies.add(occId);
        for (const cp of (item?.occupancy_id?.chargeposting || [])) {
          allChargePostings.push({ ...cp, _roomNo: roomNo });
        }
        for (const oc of (item?.occupancy_id?.occupancycharge || [])) {
          if (oc.charge_type === "GUEST_DISBURSEMENT") {
            allGuestDisbursements.push({ ...oc, _roomNo: roomNo });
          } else if (oc.charge_type === "PAID_OUT") {
            allPaidOuts.push({ ...oc, _roomNo: roomNo });
          }
        }
      }
    }

    // --- STAY PAGE ---
    const hasStay = invoice_items.some(
      (it) => it.invoice_item_price > 0 || it.invoice_item_addons?.length > 0
    );
    if (hasStay) {
      let stayGross = 0;
      const getStaySubtotal = (price, taxes, accum) => {
        let sub = new Decimal_(price || 0);
        let taxSum = (taxes || []).reduce(
          (s, tx) => s.plus(new Decimal_(tx?.invoice_tax_amount || tx?.addon_tax_amount || 0)),
          new Decimal_(0)
        );
        const val = Number(sub.plus(taxSum)).toFixed(2);
        if (accum) stayGross += Number(val);
        return val;
      };

      const allStayTaxRates = [];
      for (const room of invoice_items) {
        for (const tx of (room?.invoice_taxes || [])) {
          allStayTaxRates.push(parseFloat(tx?.tax_rate));
        }
        for (const addon of (room?.invoice_item_addons || [])) {
          for (const tx of (addon?.invoice_item_addon_taxes || [])) {
            allStayTaxRates.push(parseFloat(tx?.addon_tax_rate));
          }
        }
      }
      const stayUniformRate = allStayTaxRates.length > 0 && allStayTaxRates.every((r) => r === allStayTaxRates[0])
        ? allStayTaxRates[0] : null;

      const stayRows = invoice_items
        .map((room, ind) => {
          const roomNo = room?.room_id?.room_no || room?.occupancy_id?.room_id?.room_no || "-";
          const sepOccAddons = room?.occupancy_id?.addons || [];
          const addonRows = room?.invoice_item_addons
            ?.map((per, pi) => {
              const addonName = sepOccAddons[pi]?.addon_name || "";
              const epLabel = addonName ? `${addonName} - ${t("pages.invoice_page.extra_persons")}` : `${t("pages.invoice_page.extra_persons")} ${pi + 1}`;
              return `<tr>
              <td></td><td>${roomNo}</td>
              <td>${epLabel}</td><td></td>
              <td style="text-align:right;">₹ ${parseFloat(per.addon_price).toFixed(2)}</td>
              <td style="text-align:center;">${per?.stay_days}</td>
              <td style="text-align:right;">₹ ${parseFloat(per.addon_price * per?.stay_days).toFixed(2)}</td>
              ${per?.invoice_item_addon_taxes?.map((tx) => `<td style="text-align:right;">₹ ${Number(tx?.addon_tax_amount).toFixed(2)}${stayUniformRate === null ? "<br/>" + tx?.addon_tax_rate + "%" : ""}</td>`).join("")}
              <td style="text-align:right;">₹ ${getStaySubtotal(per?.addon_total_price, per?.invoice_item_addon_taxes, true)}</td>
            </tr>`;
            })
            .join("") || "";

          return `<tr>
            <td style="text-align:center;">${ind + 1}</td>
            <td>${roomNo}</td>
            <td class="desc-cell">${room?.room_id?.room_type_id?.room_type_name || ""}<br/>
              ${format(this.getLocalTimeFromUtc(room.checkin_date?.replace("T", " ").split(".")[0]).dateTimeString, "dd-MM-yyyy hh:mm a")}<br/>
              ${format(this.getLocalTimeFromUtc(room.checkout_date?.replace("T", " ").split(".")[0]).dateTimeString, "dd-MM-yyyy hh:mm a")}
              ${Number(room.discount_amount) > 0 ? `<br/><span style="color:#d32f2f;">${t("Discount")}: -₹ ${parseFloat(room.discount_amount).toFixed(2)}</span>` : ""}
            </td>
            <td>${supplier_hsn_sac}</td>
            <td style="text-align:right;">₹ ${parseFloat(room.invoice_item_price).toFixed(2)}</td>
            <td style="text-align:center;">${room?.stay_days}</td>
            <td style="text-align:right;">₹ ${parseFloat(room.invoice_item_price * room?.stay_days).toFixed(2)}</td>
            ${room?.invoice_taxes?.map((tx) => `<td style="text-align:right;">₹ ${Number(tx?.invoice_tax_amount).toFixed(2)}${stayUniformRate === null ? "<br/>" + parseFloat(tx?.tax_rate).toFixed(2) + "%" : ""}</td>`).join("")}
            <td style="text-align:right;">₹ ${getStaySubtotal(room?.invoice_item_total_price, room?.invoice_taxes, true)}</td>
          </tr>
          ${addonRows}`;
        })
        .join("");

      const stayBody = `<div>
        <table class="invoice-table">
          <thead><tr>
            <th style="text-align:center;">${t("pages.invoice_page.s_no")}</th>
            <th>${t("Room No")}</th>
            <th>${t("pages.invoice_page.description")}</th>
            <th>${t("pages.invoice_page.hsn_sac")}</th>
            <th style="text-align:right;">${t("pages.invoice_page.base_price")}</th>
            <th style="text-align:center;">${t("pages.invoice_page.days")}</th>
            <th style="text-align:right;">${t("pages.invoice_page.total_base_price")}</th>
            <th style="text-align:right;">${t("pages.invoice_page.sgst")}${stayUniformRate !== null ? "<br/>" + (stayUniformRate / 2).toFixed(2) + "%" : ""}</th>
            <th style="text-align:right;">${t("pages.invoice_page.cgst")}${stayUniformRate !== null ? "<br/>" + (stayUniformRate / 2).toFixed(2) + "%" : ""}</th>
            <th style="text-align:right;">${t("pages.invoice_page.net_rent")}</th>
          </tr></thead>
          <tbody>
            ${stayRows}
            <tr><td colspan="8"></td><td style="color:#252525;">${t("pages.invoice_page.subtotal")}</td><td style="color:#3D3D3D;font-weight:600;text-align:right;">₹ ${stayGross.toFixed(2)}</td></tr>
          </tbody>
        </table>
        <div class="table-container"><table class="summary-table"><tbody>
          <tr><td style="padding-right:10px;">${t("pages.invoice_page.total")}</td><td style="padding-left:10px;color:var(--primary-color);text-align:right;font-weight:600">₹ ${stayGross.toFixed(2)}</td></tr>
        </tbody></table></div>
      </div>`;
      templates.push(
        this._buildInvoicePageShell(data, invoiceData, "", stayBody)
      );
    }

    // --- CHARGES POSTING PAGES (one per outlet type) ---
    if (allChargePostings.length > 0) {
      const outletGroups = {};
      for (const cp of allChargePostings) {
        const outletName = cp?.outlet?.outlet_name || "Other";
        if (!outletGroups[outletName]) outletGroups[outletName] = [];
        outletGroups[outletName].push(cp);
      }

      for (const [outletName, cps] of Object.entries(outletGroups)) {
        let cpSubtotal = 0;
        const allCpTaxRates = cps.map((cp) => parseFloat(cp?.tax_percent || 0));
        const cpUniformRate = allCpTaxRates.length > 0 && allCpTaxRates.every((r) => r === allCpTaxRates[0])
          ? allCpTaxRates[0] : null;

        const cpRows = cps
          .map((cp, i) => {
            const cpTotal = parseFloat(cp?.total || 0);
            const cpRawAmount = parseFloat(cp?.amount || 0);
            const taxPercent = parseFloat(cp?.tax_percent || 0);
            let cpAmount, cpTax;
            if (cp?.incl_tax && taxPercent > 0) {
              cpAmount = parseFloat((cpTotal / (1 + taxPercent / 100)).toFixed(2));
              cpTax = parseFloat((cpTotal - cpAmount).toFixed(2));
            } else {
              cpAmount = cpRawAmount;
              cpTax = cpTotal - cpRawAmount;
            }
            const sgst = cpTax > 0 ? (cpTax / 2).toFixed(2) : "0.00";
            const cgst = cpTax > 0 ? (cpTax / 2).toFixed(2) : "0.00";
            cpSubtotal += cpTotal;
            return `<tr>
              <td style="text-align:center;">${i + 1}</td>
              <td>${cp._roomNo}</td>
              <td>${outletName}</td>
              <td class="desc-cell">${cp?.description || "-"}</td>
              <td></td>
              <td style="text-align:right;">₹ ${cpAmount.toFixed(2)}</td>
              <td style="text-align:right;">₹ ${cgst}${cpUniformRate === null && cp?.incl_tax && cp?.tax_percent ? "<br/>" + (parseFloat(cp.tax_percent) / 2).toFixed(2) + "%" : ""}</td>
              <td style="text-align:right;">₹ ${sgst}${cpUniformRate === null && cp?.incl_tax && cp?.tax_percent ? "<br/>" + (parseFloat(cp.tax_percent) / 2).toFixed(2) + "%" : ""}</td>
              <td style="text-align:right;">₹ ${cpTotal.toFixed(2)}</td>
            </tr>`;
          })
          .join("");

        const cpBody = `<div>
          <table class="invoice-table">
            <thead><tr>
              <th style="text-align:center;">${t("pages.invoice_page.s_no")}</th>
              <th>${t("Room No")}</th>
              <th>${t("Outlet")}</th>
              <th>${t("pages.invoice_page.description")}</th>
              <th>${t("pages.invoice_page.hsn_sac")}</th>
              <th style="text-align:right;">${t("Base Amount")}</th>
              <th style="text-align:right;">${t("pages.invoice_page.cgst")}${cpUniformRate !== null && cpUniformRate > 0 ? "<br/>" + (cpUniformRate / 2).toFixed(2) + "%" : ""}</th>
              <th style="text-align:right;">${t("pages.invoice_page.sgst")}${cpUniformRate !== null && cpUniformRate > 0 ? "<br/>" + (cpUniformRate / 2).toFixed(2) + "%" : ""}</th>
              <th style="text-align:right;">${t("Net Amount")}</th>
            </tr></thead>
            <tbody>
              ${cpRows}
              <tr><td colspan="7"></td><td style="color:#252525;">${t("pages.invoice_page.subtotal")}</td><td style="color:#3D3D3D;font-weight:600;text-align:right;">₹ ${cpSubtotal.toFixed(2)}</td></tr>
            </tbody>
          </table>
          <div class="table-container"><table class="summary-table"><tbody>
            <tr><td style="padding-right:10px;">${t("pages.invoice_page.total")}</td><td style="padding-left:10px;color:var(--primary-color);text-align:right;font-weight:600">₹ ${cpSubtotal.toFixed(2)}</td></tr>
          </tbody></table></div>
        </div>`;
        templates.push(
          this._buildInvoicePageShell(
            data,
            invoiceData,
            "",
            cpBody
          )
        );
      }
    }

    // --- PAID OUT PAGES (one per paid out type) ---
    if (allPaidOuts.length > 0) {
      const poGroups = {};
      for (const po of allPaidOuts) {
        const typeName = po?.paid_out_type?.paid_out_type_name || "Other";
        if (!poGroups[typeName]) poGroups[typeName] = [];
        poGroups[typeName].push(po);
      }

      for (const [typeName, pos] of Object.entries(poGroups)) {
        let poSubtotal = 0;
        const poRows = pos
          .map((po, i) => {
            const amt = parseFloat(po?.amount || 0);
            poSubtotal += amt;
            return `<tr>
              <td style="text-align:center;">${i + 1}</td>
              <td>${po._roomNo}</td>
              <td>${typeName}</td>
              <td></td>
              <td style="text-align:right;">₹ ${amt.toFixed(2)}</td>
            </tr>`;
          })
          .join("");

        const poBody = `<div>
          <table class="invoice-table">
            <thead><tr>
              <th style="text-align:center;">${t("pages.invoice_page.s_no")}</th>
              <th>${t("Room No")}</th>
              <th>${t("Paid Out Type")}</th>
              <th>${t("pages.invoice_page.hsn_sac")}</th>
              <th style="text-align:right;">${t("Amount")}</th>
            </tr></thead>
            <tbody>
              ${poRows}
              <tr><td colspan="3"></td><td style="color:#252525;">${t("pages.invoice_page.subtotal")}</td><td style="color:#3D3D3D;font-weight:600;text-align:right;">₹ ${poSubtotal.toFixed(2)}</td></tr>
            </tbody>
          </table>
          <div class="table-container"><table class="summary-table"><tbody>
            <tr><td style="padding-right:10px;">${t("pages.invoice_page.total")}</td><td style="padding-left:10px;color:var(--primary-color);text-align:right;font-weight:600">₹ ${poSubtotal.toFixed(2)}</td></tr>
          </tbody></table></div>
        </div>`;
        templates.push(
          this._buildInvoicePageShell(
            data,
            invoiceData,
            "",
            poBody
          )
        );
      }
    }

    // --- GUEST DISBURSEMENT PAGES (one per disbursement type) ---
    if (allGuestDisbursements.length > 0) {
      const gdGroups = {};
      for (const gd of allGuestDisbursements) {
        const typeName =
          gd?.disbursement_type?.disbursement_type_name || "Other";
        if (!gdGroups[typeName]) gdGroups[typeName] = [];
        gdGroups[typeName].push(gd);
      }

      for (const [typeName, gds] of Object.entries(gdGroups)) {
        let gdSubtotal = 0;
        const gdRows = gds
          .map((gd, i) => {
            const amt = parseFloat(gd?.amount || 0);
            gdSubtotal += amt;
            return `<tr>
              <td style="text-align:center;">${i + 1}</td>
              <td>${gd._roomNo}</td>
              <td>${typeName}</td>
              <td></td>
              <td style="text-align:right;">₹ ${amt.toFixed(2)}</td>
            </tr>`;
          })
          .join("");

        const gdBody = `<div>
          <table class="invoice-table">
            <thead><tr>
              <th style="text-align:center;">${t("pages.invoice_page.s_no")}</th>
              <th>${t("Room No")}</th>
              <th>${t("Guest Disbursement Type")}</th>
              <th>${t("pages.invoice_page.hsn_sac")}</th>
              <th style="text-align:right;">${t("Amount")}</th>
            </tr></thead>
            <tbody>
              ${gdRows}
              <tr><td colspan="3"></td><td style="color:#252525;">${t("pages.invoice_page.subtotal")}</td><td style="color:#3D3D3D;font-weight:600;text-align:right;">₹ ${gdSubtotal.toFixed(2)}</td></tr>
            </tbody>
          </table>
          <div class="table-container"><table class="summary-table"><tbody>
            <tr><td style="padding-right:10px;">${t("pages.invoice_page.total")}</td><td style="padding-left:10px;color:var(--primary-color);text-align:right;font-weight:600">₹ ${gdSubtotal.toFixed(2)}</td></tr>
          </tbody></table></div>
        </div>`;
        templates.push(
          this._buildInvoicePageShell(
            data,
            invoiceData,
            "",
            gdBody
          )
        );
      }
    }

    return templates;
  };

  generateGRCTemplate = (data) => {
    const t = i18next.t;
    try {
      const {
        hotel_logo,
        hotel_name,
        hotel_address,
        hotel_email,
        hotel_contact_number,
        hotel_gstin_number,
      } = data?.userData || {};

      const {
        name,
        mobile,
        source_type_name,
        booked_rooms,
        address,
        occupation,
        city_name,
        state_name,
        country_name,
        grc_number,
      } = data?.bookingData || {};

      const template = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Guest Registration Card</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        background-color: #FFFFFF;
      }
      .container {
        background: #fff;
        padding: 2.5%;
        border-radius: 5px;
        margin: auto;
      }
      .header {
        margin-bottom: 4%;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        line-height: 10px;
        width: 96%;
        margin: auto;
      }
      .header p {
        font-size: 12px;
        line-height: 1rem;
        width: 50%;
      }
      .info {
        margin-top: 5%;
      }
      .guest-info, .room-details {
        width: 96%;
        border-collapse: collapse;
        border: 1px solid #e9e9e9;
      }
      .info td, .guest-info td {
        padding: 5px 10px;
        border: none;
      }
      .guest-info td {
        padding: 8px;
        border: 1px solid #ccc;
        border: 1px solid #e9e9e9;
        font-size: 12px;
      }
      .guest-info .label {
        background: linear-gradient(180deg, #DEA829 0%, #FE8888 100%);
        font-weight: bold;
        text-align: left;
        width: 18%;
        color: white;
      }
      .hotel-info {
        display: flex;
        align-items: center;
        column-gap: 1rem;
      }
      .room-details {
        border: 1px solid #e9e9e9;
        margin: 0 0 5% 0;
      }
      .room-details th {
        background: linear-gradient(180deg, #DEA829 0%, #FE8888 100%);
        color: white;
        font-size: 10px;
        padding: 10px;
      }
      .room-details td {
        padding: 10px;
        text-align: center;
        font-size: 10px;
        border-bottom: 1px solid #e9e9e9;
      }
      .declaration {
        font-size: 12px;
        text-align: center;
        margin-top: 20px;
        color: #838383;
      }
      .signature {
        font-size: 12px;
        font-weight: 700;
        text-align: right;
        margin-top: 30px;
      }
      h5 {
        font-size: 18px;
        font-weight: 700;
      }
      h6 {
        font-size: 14px;
        font-weight: 700;
        margin: 5% 0 1% 0;
      }
      .logo {
        height: 44px;
        width: 44px;
      }
      @media (max-width: 600px) {
        .container {
          padding: 10px;
        }
        .info td, .guest-info td, .room-details th, .room-details td {
          padding: 5px;
          font-size: 14px;
        }
      }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="header">
        <div>
          <div class="hotel-info">
            <img src="${hotel_logo}" class="logo" alt="Hotel Logo" />
            <h5>${hotel_name}</h5>
          </div>
          <p>${hotel_address}</p>
          <p>${hotel_contact_number}</p>
          <p>${hotel_email}</p>
        </div>
        <div>
          <table class="info">
            <tr>
              <td><strong>GRC Number:</strong> ${grc_number}</td>
            </tr>
            <tr>
              <td><strong>GST No:</strong> ${hotel_gstin_number}</td>
            </tr>
          </table>
        </div>
      </div>

      <h6>Guest Information</h6>
        <table class="guest-info">
        <tr>
          <td class="label">Name</td>
          <td>${name}</td>
          <td class="label">Occupation</td>
          <td>${occupation || "-"}</td>
        </tr>
        <tr>
          <td class="label">Address</td>
          <td>${address}</td>
          <td class="label">Booking Source</td>
          <td>${source_type_name}</td>
        </tr>
        <tr>
          <td class="label">State</td>
          <td>${state_name}</td>
          <td class="label">Purpose of Visit</td>
          <td>${data?.bookingData?.booked_rooms?.[0]?.purpose_of_visit || "-"
        }</td>
        </tr>
        <tr>
          <td class="label">City</td>
          <td>${city_name}</td>
          <td class="label">Country</td>
          <td>${country_name}</td>
        </tr>
        <tr>
          <td class="label">Contact</td>
          <td colspan="3">${mobile}</td>
        </tr>
      </table>

      <h6>Room Information</h6>
      <table class="room-details">
        <tr>
          <th>S.No</th>
          <th>Room No</th>
          <th>Room Type</th>
          <th>Rate Plan</th>
          <th>PAX</th>
          <th>Extra</th>
          <th>Rent</th>
          <th>Checked-in Date</th>
          <th>Checked-out Date</th>
        </tr>
        ${booked_rooms
          ?.map(
            (r, ind) =>
              `<tr>
                <td>${ind + 1}</td>
                <td>${r.room_no}</td>
                <td>${r.room_type_name}</td>
                <td>${r.rate_plan_name || "-"}</td>
                <td>${Number(r.no_of_adults || 0) + Number(r.no_of_children || 0)
              }</td>
                <td>${r.extra_persons?.length || "-"}</td>
                <td>₹${this.roundOff(this.getRoomNetRent(r))}</td>
                <td>${this.getLocalTimeFromUtc(r.checkin_date).dateString}</td>
                <td>${this.getLocalTimeFromUtc(
                r.actual_checkout_date || r.expected_checkout_date
              ).dateString
              }</td>
              </tr>`
          )
          .join("")} 
      </table>
      <p class="declaration">I hereby declare that the above information is true to my knowledge.</p>
      <p class="signature">Guest Signature</p>
    </div>
  </body>
</html>
`;
      return template;
    } catch (error) {
      console.error("error in generate invoice template:", error);
    }
  };

  newPrintPaymentReceipt(billData) {
    try {
      const helper = new Helper();
      let printableTemplate = "";
      const billingMode = billData?.billingMode || "group";

      if (billData?.billHeader === "INVOICE") {
        billData["billHeader"] = "TAX INVOICE";

        if (billingMode === "separate") {
          billData.invoiceData.forEach((invoiceDatum) => {
            const separatePages =
              helper.generateSeparateInvoiceTemplates(billData, invoiceDatum);
            separatePages.forEach((page) => {
              printableTemplate += page;
              printableTemplate +=
                '<div style="page-break-after: always;"></div>';
            });
          });
        } else {
          billData.invoiceData.forEach((invoiceDatum) => {
            printableTemplate += helper.generateInvoiceTemplate(
              billData,
              invoiceDatum
            );
            printableTemplate +=
              '<div style="page-break-after: always;"></div>';
          });
        }
      } else if (billData?.billHeader === "GRC") {
        printableTemplate = helper.generateGRCTemplate(billData);
      } else {
        printableTemplate = helper.generateReceiptTemplate(billData);
      }

      // Open the print window
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        const printableContent = `
          <html>
            <head>
              <style>
                @media print {
                  .page-break {
                    page-break-after: always;
                  }
                }
              </style>
            </head>
            <body>
              ${printableTemplate}
            </body>
          </html>
        `;

        // Write content to the print window
        printWindow.document.open();
        printWindow.document.write(printableContent);
        printWindow.document.close();

        // Focus, print, and close the window
        printWindow.focus();

        // Function to check if all images are loaded
        const allImagesLoaded = () => {
          try {
            const images = printWindow.document.getElementsByTagName("img");
            for (let i = 0; i < images.length; i++) {
              if (!images[i].complete) {
                return false;
              }
            }
            return true;
          } catch (error) {
            console.error("Error checking image load status:", error);
            return false;
          }
        };
        const checkAndPrint = () => {
          try {
            if (allImagesLoaded()) {
              printWindow.print();
              setTimeout(() => {
                printWindow.close();
              }, 1000);
            } else {
              setTimeout(checkAndPrint, 100);
            }
          } catch (error) {
            console.error("Error during print operation:", error);
          }
        };

        // Initiate the check and print process
        checkAndPrint();
      } else {
        console.error("Failed to open print window.");
        alert(
          "Unable to open print window. Please check your browser settings."
        );
      }
    } catch (error) {
      console.error("An error occurred while printing:", error);
      alert("An error occurred while preparing the print. Please try again.");
    }
  }

  // printTableData = (tableType, records, userData) => {
  //   let printableTemplate = this.generateTableData(tableType, records, userData);
  //   let iframe = document.createElement("iframe");
  //   iframe.setAttribute("style", "visibility: hidden;");
  //   document.body.appendChild(iframe);
  //   let iframeDoc = iframe.contentWindow.document;
  //   iframeDoc.open();
  //   iframeDoc.write(printableTemplate);
  //   iframeDoc.close();
  //   iframe.onload = function () {
  //     iframe.contentWindow.print();
  //     document.body.removeChild(iframe);
  //   };
  // };
  printTableData = (tableType, allFilter, records, userData) => {
    try {
      // Generate the printable template
      const printableTemplate = this.generateTableData(
        tableType,
        allFilter,
        records,
        userData
      );

      // Open a new window for printing
      const printWindow = window.open("", "Print Table");
      if (!printWindow) {
        console.error(
          "Failed to open print window. Please check your browser settings."
        );
        alert(
          "Unable to open the print window. Ensure pop-ups are not blocked."
        );
        return;
      }

      // Write the template into the print window
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Table</title>
            <style>
              @media print {
                .page-break {
                  page-break-after: always;
                }
              }
            </style>
          </head>
          <body>
            ${printableTemplate}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();

      // Function to check if all images are loaded
      const allImagesLoaded = () => {
        try {
          const images = printWindow.document.getElementsByTagName("img");
          for (let i = 0; i < images.length; i++) {
            if (!images[i].complete) {
              return false;
            }
          }
          return true;
        } catch (error) {
          console.error("Error checking image load status:", error);
          return false;
        }
      };

      // Recursive function to wait for images to load and then print
      const checkAndPrint = () => {
        try {
          if (allImagesLoaded()) {
            printWindow.print();
            setTimeout(() => {
              printWindow.close(); // Close the window after printing
            }, 1000);
          } else {
            setTimeout(checkAndPrint, 100); // Check every 100ms
          }
        } catch (error) {
          console.error("Error during print operation:", error);
        }
      };

      // Initiate the check and print process
      checkAndPrint();
    } catch (error) {
      console.error("An error occurred during the print operation:", error);
      alert("An error occurred while preparing the print. Please try again.");
    }
  };
  generateTableData = (tableType, allFilter, records, userData) => {
    let serialNumber = 0;

    const tableRows = records?.reduce(
      (acc, e) =>
        acc +
        e?.booked_rooms.reduce((innerAcc, room, roomInd) => {
          serialNumber++;
          const isDeparturePrint = tableType === "departure" || allFilter === "DEPARTURE";
          const signatureImageUrl = isDeparturePrint
            ? room?.checkout_signature_image?.[0]?.url
            : room?.checkin_signature_image?.[0]?.url;
          return (
            innerAcc +
            `
          <tr>
            <td style="border: 1px solid #dddddd; text-align: center; padding: 8px;">
              ${serialNumber}
            </td>
            <td style="border: 1px solid #dddddd; text-align: center; padding: 8px;">
              ${room?.room_no}
            </td>
            <td style="border: 1px solid #dddddd; text-align: left; padding: 8px;">
              ${e?.name}
            </td>
            <td style="border: 1px solid #dddddd; text-align: center; padding: 6px;">
              ${e?.mobile}
            </td>
            <td style="border: 1px solid #dddddd; text-align: center; padding: 4px; width: 70px; max-width: 70px; word-break: break-word; font-size: 12px;">
              ${e?.occupation || ""}
            </td>
            <td style="border: 1px solid #dddddd; text-align: left; padding: 8px;">
              ${e?.address}, ${e?.city_name}, ${e?.state_name}.
            </td>
            <td style="border: 1px solid #dddddd; text-align: center; padding: 8px;">
              ${room?.no_of_adults + room?.no_of_children}
            </td>
            <td style="border: 1px solid #dddddd; text-align: left; padding: 4px; width: 75px; max-width: 75px; word-break: break-word; font-size: 12px;">
              ${room?.purpose_of_visit || "-"}
            </td>
            <td style="border: 1px solid #dddddd; text-align: center; padding: 8px; white-space: nowrap;">
              ${this.getLocalTimeFromUtc(room?.checkin_date)?.date} ${this.getLocalTimeFromUtc(room?.checkin_date)?.time
            }<br/>
            ${tableType === "departure" || allFilter === "DEPARTURE"
              ? `to <br/> ${this.getLocalTimeFromUtc(room?.actual_checkout_date)?.date
              } ${this.getLocalTimeFromUtc(room?.actual_checkout_date)?.time
              }`
              : ""
            }
            </td>
            ${isDeparturePrint
              ? `
            <td style="border: 1px solid #dddddd; text-align: left; padding: 4px; width: 70px; max-width: 70px; word-break: break-word;">
              ${room?.heading_to || "-"}
            </td>`
              : ""
            }
            <td style="border: 1px solid #dddddd; text-align: center; padding: 3px; width: 160px; min-width: 160px;">
              ${signatureImageUrl
              ? `<img src="${signatureImageUrl}" alt="Signature" style="display: block; width: 155px; height: 85px; object-fit: contain; margin: 0 auto;"/>`
              : "-"
            }
            </td>
          </tr>`
          );
        }, ""),
      ""
    );
    const t = i18next.t;
    const translatedTableType =
      tableType === "all" && allFilter !== "ALL"
        ? `${t(`tableType.${tableType}`)?.toUpperCase()}-${t(
          `allFilter.${allFilter}`
        ).toUpperCase()}`
        : t(`tableType.${tableType}`)?.toUpperCase();
    return `
      <html>
        <body>
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.2%;">
          <h3>${translatedTableType}</h3>
            <div style="cursor: pointer" onclick="window.close()">[X]</div>
          </div>
          <div style="text-align: center;">
            <h1 style="letter-spacing: 1px;">${userData.hotel_name}</h1>
            <h3>${userData.hotel_address}</h3>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.5%; margin: 2% 0;">
            <div style="display: flex; justify-content: space-between; align-items: center; "><div>${t(
      "pages.invoice_page.date"
    )}&nbsp;</div> <div> : ${moment().format("DD/MM/YYYY")}</div></div>
            <div style="display: flex; justify-content: space-between; align-items: center; "><div>${t(
      "pages.invoice_page.contact_no"
    )}&nbsp;</div> <div> : ${userData?.hotel_contact_number}</div></div>
            <div style="display: flex; justify-content: space-between; align-items: center; "><div>${t(
      "pages.invoice_page.email"
    )}&nbsp;</div> <div> : ${userData?.hotel_email}</div></div>
          </div>
          <table id="table" style="border-collapse: collapse; width: 100%; page-break-after: auto;">
            <tr style="border-bottom: 2px solid black; border-top: 2px solid black; page-break-inside: avoid; page-break-after: auto;">
              <th style="border: 1px solid #dddddd; text-align: center; padding: 8px;">${t(
      "pages.invoice_page.s_no"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: left; padding: 8px; white-space: nowrap;">${t(
      "pages.invoice_page.room_no"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: left; padding: 8px;">${t(
      "pages.invoice_page.name"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: center; padding: 6px;">${t(
      "pages.invoice_page.mobile"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: left; padding: 4px; width: 70px; max-width: 70px;">${t(
      "pages.invoice_page.occupation"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: left; padding: 8px; min-width: 180px !important;">${t(
      "pages.invoice_page.address"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: center; padding: 8px;">${t(
      "pages.invoice_page.pax"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: left; padding: 4px; width: 75px; max-width: 75px; white-space: normal;">${t(
      "pages.invoice_page.purpose"
    )}</th>
              <th style="border: 1px solid #dddddd; text-align: center; padding: 5px; white-space: nowrap;">${t(
      "pages.invoice_page.date"
    )}</th>
              ${tableType === "departure" || allFilter === "DEPARTURE"
        ? `
          <th style="border: 1px solid #dddddd; text-align: left; padding: 4px; width: 70px; max-width: 70px; white-space: normal;">${t(
          "pages.invoice_page.heading_to"
        )}</th>`
        : ""
      }
              <th style="border: 1px solid #dddddd; text-align: center; padding: 3px; width: 160px; min-width: 160px;">${t(
        "pages.invoice_page.signature"
      )}</th>
            </tr>
            ${tableRows}
          </table>
        </body>
      </html>`;
  };

  printImage = (imageUrl) => {
    const printWindow = window.open("", "_blank");

    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Print Image</title>
            <style>
                body {
                    margin: 0;
                    padding: 0;
                    text-align: center;
                    background-color: white;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                }
                .print-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                }
                .print-container img {
                    max-width: 100%;
                    max-height: 100vh;
                    box-shadow: 4px 4px 10px rgba(0, 0, 0, 0.5);
                }
                @media print {
                    body { background-color: white; }
                    .print-container { height: auto; }
                    img { max-width: 100%; max-height: 100vh; }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <img src="${imageUrl}" alt="Printable Image" onload="window.print(); window.onafterprint = () => window.close();" />
            </div>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  getUTCDateTime = (localTime) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!localTime) {
        return null;
      }
      const utcTime = fromZonedTime(localTime, timezone);
      return utcTime;
    } catch (error) {
      throw error;
    }
  };

  getLocalTimeFromUtc = (utcTime) => {
    try {
      const utcDateTime = utcTime?.replace(" ", "T").split(".")[0] + "Z";
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const localTime = toZonedTime(utcDateTime, timeZone);
      const dateString = format(localTime, "yyyy-MM-dd");
      const dateTimeString = format(localTime, "yyyy-MM-dd HH:mm:ss");
      const date = format(localTime, "MMM dd yyyy");
      const time = format(localTime, "hh:mm a");
      const ddmmyyyy = format(localTime, "dd-MM-yyyy");
      const isoDateTimeString = format(localTime, "yyyy-MM-dd'T'HH:mm");
      return {
        date,
        time,
        dateString,
        dateTimeString,
        localTime,
        ddmmyyyy,
        isoDateTimeString,
      };
    } catch (error) {
      // console.error(error);
    }
  };

  calculateStayDays = (expected_checkin_date, expected_checkout_date) => {
    const checkinDate = new Date(
      new Date(expected_checkin_date).setHours(0, 0, 0, 0)
    );
    const checkoutDate = new Date(
      new Date(expected_checkout_date).setHours(0, 0, 0, 0)
    );

    const stayDays = Math.ceil(
      (checkoutDate.getTime() - checkinDate.getTime()) / (24 * 60 * 60 * 1000)
    );

    return !!stayDays || stayDays === 0 ? stayDays + 1 : 0;
  };

  getDaysDifferenceByStayType = (
    startDate,
    endDate,
    stayType,
    checkinGraceHour = 0,
    checkoutGraceHour = 0,
    standardCheckinTime,
    standardCheckoutTime
  ) => {
    try {
      // Ensure null values default to 0
      checkinGraceHour = checkinGraceHour || 0;
      checkoutGraceHour = checkoutGraceHour || 0;

      const sDate = startDate instanceof Date ? startDate : new Date(startDate);
      const eDate = endDate instanceof Date ? endDate : new Date(endDate);
      let startDateIso = parseISO(format(sDate, "yyyy-MM-dd'T'HH:mm:ss"));
      let endDateIso = parseISO(format(eDate, "yyyy-MM-dd'T'HH:mm:ss"));

      // Apply checkout grace hour to both stay types
      // endDateIso = addHours(endDateIso, checkoutGraceHour);
      endDateIso = subHours(endDateIso, checkoutGraceHour);

      if (stayType === STAY_TYPES.DAY) {
        const dayStayMinDiff = differenceInMinutes(endDateIso, startDateIso);
        return Math.ceil(dayStayMinDiff / 1440); // Convert to days
      } else if (stayType === STAY_TYPES.NOON) {
        if (
          startDateIso.getFullYear() === endDateIso.getFullYear() &&
          startDateIso.getMonth() === endDateIso.getMonth() &&
          startDateIso.getDate() === endDateIso.getDate()
        ) {
          return 1;
        }

        const noonStartDate = addHours(startOfDay(startDateIso), 12); // Set to 12 PM

        // Apply check-in grace hour for Noon Stay
        startDateIso = addHours(startDateIso, checkinGraceHour);

        let beforeDay = 0;
        if (
          isBefore(startDateIso, noonStartDate) &&
          isBefore(noonStartDate, endDateIso)
        ) {
          const beforeDayDiffMin = differenceInMinutes(
            noonStartDate,
            startDateIso
          );
          beforeDay = Math.max(1, Math.ceil(beforeDayDiffMin / 1440));
        }

        const totalMinutes = differenceInMinutes(endDateIso, noonStartDate);
        const adjustedMinutes = Math.max(0, totalMinutes);
        const days = Math.ceil(adjustedMinutes / 1440);

        return beforeDay + (days > 0 ? days : 1);
      } else if (stayType === STAY_TYPES.STANDARD) {
        if (
          startDateIso.getFullYear() === endDateIso.getFullYear() &&
          startDateIso.getMonth() === endDateIso.getMonth() &&
          startDateIso.getDate() === endDateIso.getDate()
        ) {
          return 1;
        }

        const boundaryTime = this.applyBoundaryTime(startDateIso, standardCheckoutTime || '12:00:00');
        const standardStartDate = parseISO(format(boundaryTime, "yyyy-MM-dd'T'HH:mm:ss"));

        startDateIso = addHours(startDateIso, checkinGraceHour);

        let beforeDay = 0;
        if (
          isBefore(startDateIso, standardStartDate) &&
          isBefore(standardStartDate, endDateIso)
        ) {
          const beforeDayDiffMin = differenceInMinutes(
            standardStartDate,
            startDateIso
          );
          beforeDay = Math.max(1, Math.ceil(beforeDayDiffMin / 1440));
        }

        const totalMinutes = differenceInMinutes(endDateIso, standardStartDate);
        const adjustedMinutes = Math.max(0, totalMinutes);
        const days = Math.ceil(adjustedMinutes / 1440);

        return beforeDay + (days > 0 ? days : 1);
      } else {
        throw new Error("Enter valid stay type");
      }
    } catch (error) {
      console.error("Error in getDaysDifferenceByStayType:", error);
      throw error;
    }
  };

  // debounce.js
  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  getPayloadFromToken = (token) => {
    try {
      if (!!token) {
        return decodeJwt(token);
      } else {
        return null;
      }
    } catch (error) {
      console.error("Invalid token:", error);
      return null;
    }
  };

  getAuthorizedObjects(componentObject, currentRole, permissions = null) {
    if (!Array.isArray(componentObject)) return [];

    const isAppAdmin =
      String(currentRole || '').toUpperCase() === 'APP_ADMIN';

    const isVisible = (option) => {
      if (isAppAdmin) return true;
      if (!option.requiredFeature) return true;
      if (!permissions) return false;
      const action = option.requiredAction || 'view';
      return !!permissions[option.requiredFeature]?.includes(action);
    };

    return componentObject
      .filter(isVisible)
      .map((option) => {
        if (!option.submenu) return option;
        return { ...option, submenu: option.submenu.filter(isVisible) };
      })
      .filter((option) => !option.submenu || option.submenu.length > 0);
  }

  sortByParentKey(roomsArray) {
    return roomsArray.sort((a, b) => {
      // Group related records together based on their parent_occupancy_key or occupancy_key
      const aKey = a.parent_occupancy_key || a.occupancy_key;
      const bKey = b.parent_occupancy_key || b.occupancy_key;

      // First, sort by group key (aKey and bKey)
      if (aKey !== bKey) {
        return aKey.localeCompare(bKey); // Using localeCompare for string comparison (UUIDs)
      }

      // If both belong to the same group, sort by occupancy_id in descending order
      return b.occupancy_id - a.occupancy_id;
    });
  }

  filterInvalidKeys(sourceObject) {
    const result = {};
    for (const key in sourceObject) {
      const value = sourceObject[key];
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== " "
      ) {
        result[key] = value;
      }
    }
    return result;
  }

  handlePrintSelectedRows = (selectedRows, reportType) => {
    if (!selectedRows || selectedRows?.length === 0) {
      warnToster("No rows selected to print!");
      return;
    }

    // Extract headers dynamically from the first row
    const headers = Object.keys(selectedRows[0]);

    // Extract rows as an array of arrays
    const rows = selectedRows.map((row) =>
      headers.map((header) => row[header])
    );

    // Function to generate table rows incrementally
    const generateTableBody = (rowsChunk) =>
      rowsChunk
        .map(
          (row) =>
            `<tr>${row
              .map(
                (cell) =>
                  `<td>${cell !== null && cell !== undefined ? cell : ""}</td>`
              )
              .join("")}</tr>`
        )
        .join("");

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      // Basic HTML structure
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>${reportType}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
              }
              th, td {
                border: 1px solid black;
                padding: 8px;
                text-align: left;
                font-size: 14px;
                max-width: 180px;
              }
              th {
                padding: 12px 8px;
                font-size: 16px;
                white-space: nowrap;
                background-color: #f2f2f2;
              }
              h1 {
                text-align: center;
                margin-bottom: 20px;
              }
            </style>
          </head>
          <body>
            <h1>${reportType}</h1>
            <table>
              <thead>
                <tr>${headers
          .map((header) => `<th>${header}</th>`)
          .join("")}</tr>
              </thead>
              <tbody id="table-body"></tbody>
            </table>
          </body>
        </html>
      `);

      // Incrementally render table rows
      const tableBody = printWindow.document.getElementById("table-body");
      let chunkSize = 50; // Number of rows to render at once
      let currentIndex = 0;

      const renderNextChunk = () => {
        const rowsChunk = rows.slice(currentIndex, currentIndex + chunkSize);
        tableBody.innerHTML += generateTableBody(rowsChunk);
        currentIndex += chunkSize;

        if (currentIndex < rows.length) {
          setTimeout(renderNextChunk, 100); // Render next chunk after a delay
        } else {
          printWindow.focus();
          printWindow.print();
          setTimeout(() => {
            printWindow.close();
          }, 3000);
        }
      };

      renderNextChunk();
    } else {
      console.error("Failed to open print window.");
      alert("Unable to open the print window. Ensure pop-ups are not blocked.");
    }
  };

  handlePrintTabularReport = (selectedRows, reportType, userData) => {
    if (!selectedRows || selectedRows?.length === 0) {
      warnToster("No rows selected to print!");
      return;
    }

    const t = i18next.t;

    // Extract headers dynamically from the first row
    const headers = Object.keys(selectedRows[0]);

    const isImageValue = (val) =>
      typeof val === "string" &&
      (/^data:image\//i.test(val) || /^https?:\/\//i.test(val));

    const renderCell = (cell) => {
      if (cell === null || cell === undefined || cell === "") return "";
      if (isImageValue(cell)) {
        return `<img src="${cell}" alt="signature" style="width: 69px; height: 35px; object-fit: contain; border: 1px solid #e0e0e0; border-radius: 4px; background-color: #ffffff;" />`;
      }
      return cell;
    };

    const bodyHtml = selectedRows
      .map(
        (row) =>
          `<tr>${headers
            .map(
              (header) =>
                `<td style="border: 1px solid #dddddd; padding: 8px; text-align: left;">${renderCell(
                  row[header]
                )}</td>`
            )
            .join("")}</tr>`
      )
      .join("");

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      // HTML structure with hotel information header
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>${reportType}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 20px;
              }
              .hotel-header {
                text-align: center;
                margin-bottom: 20px;
              }
              .hotel-header h1 {
                letter-spacing: 1px;
                margin: 10px 0;
                font-size: 24px;
              }
              .hotel-header h3 {
                margin: 5px 0;
                font-weight: normal;
                font-size: 16px;
              }
              .hotel-info {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 10px 0;
                margin: 15px 0;
                border-top: 1px solid #ddd;
                border-bottom: 1px solid #ddd;
              }
              .info-item {
                display: flex;
                gap: 5px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
              }
              th, td {
                border: 1px solid #dddddd;
                padding: 8px;
                text-align: left;
                font-size: 14px;
              }
              th {
                padding: 12px 8px;
                font-size: 16px;
                white-space: nowrap;
                background-color: #f2f2f2;
                font-weight: bold;
              }
              .report-title {
                text-align: center;
                margin: 20px 0;
                font-size: 20px;
                font-weight: bold;
              }
              @media print {
                body {
                  margin: 10px;
                }
                .hotel-header h1 {
                  font-size: 22px;
                }
              }
            </style>
          </head>
          <body>
            <div class="hotel-header">
              <h1>${userData?.hotel_name || ""}</h1>
              <h3>${userData?.hotel_address || ""}</h3>
            </div>
            <div class="hotel-info">
              <div class="info-item">
                <div>${t("pages.invoice_page.date")}:</div>
                <div>${moment().format("DD/MM/YYYY")}</div>
              </div>
              <div class="info-item">
                <div>${t("pages.invoice_page.contact_no")}:</div>
                <div>${userData?.hotel_contact_number || ""}</div>
              </div>
              <div class="info-item">
                <div>${t("pages.invoice_page.email")}:</div>
                <div>${userData?.hotel_email || ""}</div>
              </div>
            </div>
            <div class="report-title">${reportType}</div>
            <table>
              <thead>
                <tr>${headers
          .map(
            (header) =>
              `<th style="border: 1px solid #dddddd; text-align: left; padding: 12px 8px;">${header}</th>`
          )
          .join("")}</tr>
              </thead>
              <tbody>${bodyHtml}</tbody>
            </table>
            <script>
              (function () {
                function triggerPrint() {
                  try { window.focus(); window.print(); } catch (e) {}
                }
                window.onafterprint = function () { window.close(); };
                window.addEventListener("load", function () {
                  var imgs = Array.prototype.slice.call(document.images || []);
                  var fired = false;
                  function go() { if (!fired) { fired = true; triggerPrint(); } }
                  if (!imgs.length) { setTimeout(go, 50); return; }
                  var remaining = imgs.length;
                  function done() { remaining -= 1; if (remaining <= 0) go(); }
                  imgs.forEach(function (im) {
                    if (im.complete) { done(); }
                    else { im.onload = done; im.onerror = done; }
                  });
                  setTimeout(go, 5000);
                });
              })();
            <\/script>
          </body>
        </html>
      `);

      printWindow.document.close();
    } else {
      console.error("Failed to open print window.");
      alert("Unable to open the print window. Ensure pop-ups are not blocked.");
    }
  };

  addWithPadding(a, b, pad) {
    const result = (Number(a) + Number(b)).toString().padStart(pad, "0");
    return result;
  }

  fetchAndFilterVacantRooms = async (roomTypes) => {
    try {
      return roomTypes
        .map(({ rooms, ...roomType }) => ({
          ...roomType,
          rooms: rooms
            ?.filter(
              (room) =>
                room.room_id !== null &&
                !room.is_under_maintenance &&
                room.occupancy_status_code !== OCCUPANCY_STATUSES.CHECK_IN &&
                ![
                  RESERVATION_STATUSES.CONFIRMED,
                  RESERVATION_STATUSES.TENTATIVE,
                ].includes(room.reservation_status_code)
            )
            ?.map(({ room_id, room_no, room_type_id, cleaning_status_code }) => ({
              room_id,
              room_no,
              room_type_id,
              cleaning_status_code,
            })),
        }))
        .filter((roomType) => roomType.rooms.length > 0);
    } catch (error) {
      return [];
    }
  };

  getSimpleRemainingTime = (endDate) => {
    const totalMinutes = differenceInMinutes(endDate, new Date());

    if (totalMinutes <= 0) return null;

    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days} day${days !== 1 ? "s" : ""}`;
    if (hours > 0) return `${hours} hour${hours !== 1 ? "s" : ""}`;
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  };

  truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  applyPriceAdjustment = (base, rule) => {
    let adjustedBase = Number(base);
    const { adjustment_type, unit, adjustment_amount } = rule;

    if (isNaN(adjustedBase) || isNaN(adjustment_amount)) return adjustedBase;

    const delta =
      unit === "percentage"
        ? (adjustedBase * Number(adjustment_amount)) / 100
        : Number(adjustment_amount);

    let adjusted =
      adjustment_type === "increase"
        ? adjustedBase + delta
        : adjustedBase - delta;

    return Math.round(adjusted);
  };

  formatRupee = (amount) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  getGradientTextStyle = (palette, color = "gradient") => ({
    background: palette.text[color],
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  });

  getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "#FF5C5C";
      case "medium": return "#FFA500";
      case "low": return "#4CAF50";
      default: return "#757575";
    }
  };


  formatTimestampForCard = (dateInput) => {
    if (!dateInput) return "";
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return String(dateInput);
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
      }).format(date);
    } catch (e) {
      return String(dateInput);
    }
  };


  isTaskOverdue = (task) => {
    const now = new Date();
    if (!task.due_at) return false;
    return new Date(task.due_at) < now && task.status !== "Done";
  };


  isTaskDueToday = (task) => {
    const isOverdue = this.isTaskOverdue(task);
    return !isOverdue && new Date(task.due_at).toDateString() === new Date().toDateString();
  };


  sortEmployeeTasks = (tasks) => {
    return tasks.sort((a, b) => {

      if (a.moved_now && !b.moved_now) return -1;
      if (!a.moved_now && b.moved_now) return 1;
      if (a.moved_now && b.moved_now) return b.moved_now - a.moved_now;

      const aOverdue = this.isTaskOverdue(a);
      const bOverdue = this.isTaskOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;

      return new Date(a.due_at) - new Date(b.due_at);
    });
  };


  formatDateForReport = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "2-digit"
    });
  };


  formatTimeForReport = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit", minute: "2-digit", hour12: true
    });
  };


  getStatusStyle = (status) => {
    switch (status) {
      case "Done": return { bgcolor: "#D1F2D3", color: "#2E7D32" };
      case "InProgress": return { bgcolor: "#D6EAF8", color: "#2874A6" };
      case "Todo": return { bgcolor: "#FCF3CF", color: "#B7950B" };
      default: return { bgcolor: "#F5F5F5", color: "#757575" };
    }
  };


  getStatusLabel = (status, t) => {
    if (status === "Done") return t("pages.task_management.completed");
    if (status === "InProgress") return t("pages.task_management.in_progress");
    if (status === "Todo") return t("pages.task_management.todo");
    return status;
  };


  getEmployeeNameById = (id, list) => {
    const employee = list.find(emp => emp.employee_id === id);
    return employee ? employee.name : id;
  };


  combineDateAndTime = (dateStr, timeStr) => {
    const finalDate = new Date(dateStr);
    const [hours, minutes] = timeStr.split(':').map(Number);
    finalDate.setHours(hours);
    finalDate.setMinutes(minutes);
    finalDate.setSeconds(0);

    if (isNaN(finalDate.getTime())) {
      throw new Error("Invalid Date/Time Selection");
    }
    return finalDate;
  };


  getCurrentAssignedTimeStr = () => {
    const now = new Date();
    const datePart = now.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    const timePart = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${datePart}, ${timePart}`;
  }


  printReservationReceipt = (data) => {
    let printableTemplate = this.generateReservationReceiptTemplate(data);
    const printWindow = window.open("", "Print Reservation Receipt");

    printWindow.document.write(printableTemplate);
    printWindow.document.close();

    const img = printWindow.document.getElementById("hotel-logo-image");

    const triggerPrint = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 250);
    };

    if (img) {
      if (img.complete) {
        triggerPrint();
      } else {
        img.onload = triggerPrint;
        img.onerror = triggerPrint;
      }
    } else {
      triggerPrint();
    }
  };

  generateReservationReceiptTemplate = (data) => {
    const t = i18next.t;
    const { userData, reservationData } = data;
    const booking = reservationData || {};

    const hotelName = userData?.hotel_name || "Hotel Name";
    const hotelAddress = userData?.hotel_address || "Hotel Address";
    const hotelPhone = userData?.hotel_contact_number || "";
    const hotelEmail = userData?.hotel_email || userData?.email || "";
    const hotelLogo = userData?.hotel_logo || "";

    const currentDate = moment().format("DD/MM/YYYY hh:mm A");
    const statusCode = booking?.reservation_status_code || "confirmed";
    const status = statusCode
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

    const guestName = booking?.name || "Guest Name";
    const guestContact = booking?.mobile || booking?.phone || "";

    const rooms = booking?.booked_rooms || [];

    // Generate rows for each room
    const roomRows = rooms
      .map((room) => {
        const checkInDate = room.expected_checkin_date
          ? moment(room.expected_checkin_date)
          : null;
        const checkOutDate = room.expected_checkout_date
          ? moment(room.expected_checkout_date)
          : null;

        const checkIn = checkInDate ? checkInDate.format("DD-MM-YYYY") : "-";
        const checkOut = checkOutDate ? checkOutDate.format("DD-MM-YYYY") : "-";

        let nights = 0;
        if (checkInDate && checkOutDate) {
          nights = checkOutDate.diff(checkInDate, "days");
          if (nights === 0 && checkInDate.isBefore(checkOutDate)) nights = 1;
        }

        const roomType = room.room_type_name || room.room_type || "Standard";
        const roomNumber = room.room_no || "Not Assigned";
        const adults = Number(room.expected_no_of_adults || 0);
        const children = Number(room.expected_no_of_children || 0);
        const guestCount = `${adults + children}`;

        return `
            <tr>
                <td>${checkIn}</td>
                <td>${checkOut}</td>
                <td>${nights} Nights</td>
                <td>${roomType}</td>
                <td>1</td> 
                <td>${roomNumber}</td>
                <td>${guestCount}</td>
            </tr>
        `;
      })
      .join("");

    const deposit = booking?.paid_amount !== null && booking?.paid_amount !== undefined
      ? "₹" + Math.round(Number(booking.paid_amount))
      : "N/A";
    const totalAmount =
      (rooms.length > 0
        ? rooms.reduce((acc, room) => {
          const roomAmount =
            (Number(room?.reservation_price) || 0) +
            (Number(room?.total_reservation_tax_amount) || 0);
          return acc + roomAmount;
        }, 0)
        : 0);

    const formattedTotalAmount = totalAmount
      ? `₹${this.roundOff(totalAmount)}`
      : "N/A";
    const paymentMethod = booking?.payment_type || "N/A";
    const notes =
      booking?.special_request ||
      booking?.remarks ||
      booking?.purpose_of_visit ||
      "";

    const template = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Reservation Receipt</title>
      <style>
        body { font-family: Arial, sans-serif; background-color: #ffffff; color: #333; margin: 0; padding: 20px; }
        .container { max-width: 800px; margin: 0 auto; border: 1px solid #ddd; padding: 20px; }
        .header { text-align: center; margin-bottom: 20px; }
        .hotel-name { font-size: 24px; font-weight: bold; margin: 5px 0; text-transform: uppercase; }
        .hotel-details { font-size: 14px; margin: 2px 0; }
        .receipt-title { text-align: center; margin-top: 30px; margin-bottom: 20px; border-top: 2px solid #333; border-bottom: 2px solid #333; padding: 10px 0; }
        .receipt-title h2 { margin: 0; font-size: 20px; text-transform: uppercase; }
        .receipt-title p { margin: 5px 0 0; font-size: 12px; font-style: italic; }
        .info-section { margin-bottom: 20px; }
        .section-title { font-size: 16px; font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 5px; margin-bottom: 10px; color: #000; }
        .info-row { display: flex; margin-bottom: 8px; font-size: 14px; }
        .label { font-weight: bold; width: 200px; }
        .value { flex: 1; }
        .details-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px; text-align: center; }
        .details-table th { border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; }
        .details-table td { border: 1px solid #ddd; padding: 8px; }
        .footer { margin-top: 40px; text-align: center; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
        @media print {
            body { padding: 0; }
            .container { border: none; }
            .details-table th { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          ${hotelLogo
        ? "<img id='hotel-logo-image' src='" +
        hotelLogo +
        "' alt='Logo' style='height: 60px; margin-bottom: 10px;'>"
        : ""
      }
          <div class="hotel-name">${hotelName}</div>
          <div class="hotel-details">${hotelAddress}</div>
          <div class="hotel-details">Phone: ${hotelPhone} ${hotelEmail ? "| Email: " + hotelEmail : ""
      }</div>
        </div>

        <div class="receipt-title">
          <h2>RESERVATION RECEIPT / ACKNOWLEDGMENT</h2>
          <p>(This document is a reservation confirmation and not a bill.)</p>
        </div>

        <div style="display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 14px;">
            <div>
                <div><strong>Receipt Generated On:</strong> ${currentDate}</div>
            </div>
            <div><strong>Reservation Status:</strong> ${status}</div>
        </div>

        <div class="info-section">
          <div class="section-title">Guest Information</div>
          <div class="info-row"><span class="label">Guest Name:</span> <span class="value">${guestName}</span></div>
          <div class="info-row"><span class="label">Contact:</span> <span class="value">${guestContact}</span></div>
        </div>

        <div class="info-section">
          <div class="section-title">Reservation Details</div>
          <table class="details-table">
            <thead>
                <tr>
                    <th>Check-In Date</th>
                    <th>Check-Out Date</th>
                    <th>Number of Nights</th>
                    <th>Room Type</th>
                    <th>No. of Rooms</th>
                    <th>Room Number</th>
                    <th>No. of Guests</th>
                </tr>
            </thead>
            <tbody>
                ${roomRows}
            </tbody>
          </table>
        </div>

        <div class="info-section">
          <div class="section-title">Payment / Deposit (If Applicable)</div>
          <div class="info-row"><span class="label">Total Amount:</span> <span class="value">${formattedTotalAmount}</span></div>
          <div class="info-row"><span class="label">Deposit Amount Received:</span> <span class="value">${deposit}</span></div>
          <div class="info-row"><span class="label">Payment Method:</span> <span class="value">${paymentMethod}</span></div>
        </div>

        <div class="info-section">
          <div class="section-title">Notes</div>
          <div class="value">${notes || "No special requests."}</div>
        </div>

        <div class="footer">
          <p>Thank you for your reservation. We look forward to hosting you.</p>
          <p>This is an acknowledgment of reservation and not an invoice, bill, or proof of payment.</p>
        </div>
      </div>
    </body>
    </html>
    `;
    return template;
  };
  //    consolidateTaxRatesSlabs = (taxRates) => {
  //   if (!Array.isArray(taxRates)) return [];

  //   const consolidated = [];

  //   taxRates.forEach(rateSlab => {
  //     // Check if we already have a slab with same min and max
  //     const existingIndex = consolidated.findIndex(r =>
  //       Number(r.min_amount) === Number(rateSlab.min_amount) &&
  //       Number(r.max_amount) === Number(rateSlab.max_amount)
  //     );

  //     if (existingIndex !== -1) {
  //       // Add the rate to the existing matching slab in the array
  //       consolidated[existingIndex].rate = Number(consolidated[existingIndex].rate) + Number(rateSlab.rate);
  //       if (consolidated[existingIndex].code && rateSlab.code && !consolidated[existingIndex].code.includes(rateSlab.code)) {
  //         consolidated[existingIndex].code += ` + ${rateSlab.code}`;
  //       }
  //     } else {
  //       // Push a new object containing only min, max and rate to the array
  //       consolidated.push({
  //         min_amount: rateSlab.min_amount,
  //         max_amount: rateSlab.max_amount,
  //         rate: Number(rateSlab.rate),
  //         code: rateSlab.code
  //       });
  //     }
  //   });

  //   return consolidated;
  // };

  // consolidateTaxRatesSlabs = (taxRates) => {
  //   if (!Array.isArray(taxRates)) return [];

  //   const consolidated = [];

  //   taxRates.forEach(rateSlab => {
  //     const existingIndex = consolidated.findIndex(r =>
  //       Number(r.min_amount) === Number(rateSlab.min_amount) &&
  //       Number(r.max_amount) === Number(rateSlab.max_amount)
  //     );
  //     const code = rateSlab.code?.toLowerCase()
  //     if (existingIndex !== -1) {

  //       consolidated[existingIndex].rate =
  //         Number(consolidated[existingIndex].rate) + Number(rateSlab.rate);

  //       if (code === "cgst") {
  //         consolidated[existingIndex].cgst = Number(rateSlab.rate);
  //       }

  //       if (code === "sgst") {
  //         consolidated[existingIndex].sgst = Number(rateSlab.rate);
  //       }

  //       if (
  //         consolidated[existingIndex].code &&
  //         rateSlab.code &&
  //         !consolidated[existingIndex].code.includes(rateSlab.code)
  //       ) {
  //         consolidated[existingIndex].code += ` + ${rateSlab.code}`;
  //       }

  //     } else {

  //       consolidated.push({
  //         min_amount: rateSlab.min_amount,
  //         max_amount: rateSlab.max_amount,
  //         rate: Number(rateSlab.rate),
  //         cgst: code === "cgst" ? Number(rateSlab.rate) : 0,
  //         sgst: code === "sgst" ? Number(rateSlab.rate) : 0,
  //         code: rateSlab.code
  //       });

  //     }
  //   });

  //   return consolidated;
  // };
  consolidateTaxRatesSlabs = (taxRates) => {

    if (!Array.isArray(taxRates)) return [];
    const consolidated = [];
    taxRates.forEach(rateSlab => {
      const existingIndex = consolidated.findIndex(r =>
        Number(r.min_amount) === Number(rateSlab.min_amount) &&
        Number(r.max_amount) === Number(rateSlab.max_amount)
      );
      const code = rateSlab.code?.toLowerCase();
      const rate = Number(rateSlab.rate);
      if (existingIndex !== -1) {
        consolidated[existingIndex].rate =
          Number(consolidated[existingIndex].rate) + rate;
        if (code) {
          consolidated[existingIndex][code] = rate;
        }
        if (
          consolidated[existingIndex].code &&
          rateSlab.code &&
          !consolidated[existingIndex].code.includes(rateSlab.code)
        ) {
          consolidated[existingIndex].code += ` + ${rateSlab.code}`;
        }
      } else {
        const newSlab = {
          min_amount: rateSlab.min_amount,
          max_amount: rateSlab.max_amount,
          rate: rate,
          code: rateSlab.code
        };
        if (code) {
          newSlab[code] = rate;
        }
        consolidated.push(newSlab);
      }
    });

    return consolidated;
  };
  // resolveDayRentSource = (
  //   apiRent,
  //   manualEntry
  // ) => {
  //   const original_amount = apiRent.original_amount !== undefined ? apiRent.original_amount : apiRent.amount;
  //   const original_source = apiRent.original_source !== undefined ? apiRent.original_source : apiRent.source;

  //   if (!manualEntry) {
  //     return {
  //       ...apiRent,
  //       original_amount,
  //       original_source,
  //     };
  //   }

  //   const isEdited =
  //     Number(manualEntry.amount) !== Number(original_amount);

  //   return {
  //     ...apiRent,
  //     amount: manualEntry.amount,
  //     source: isEdited ? "edited" : original_source,
  //     original_amount,
  //     original_source,
  //   };
  // }
  getTaxBreakdown = (slab) => {

    const breakdown = {};
    const reservedKeys = ['min_amount', 'max_amount', 'rate', 'code'];
    Object.keys(slab || {}).forEach(key => {
      if (!reservedKeys.includes(key)) {
        breakdown[key] = slab[key];
      }
    });

    return breakdown;
  };
  getTaxPercentForAmount = (amount, taxRates, isInclusive = true) => {

    if (!Array.isArray(taxRates) || taxRates.length === 0) return 0;
    const val = Number(amount) || 0;
    const sortedSlabs = [...taxRates].sort((a, b) => Number(a.min_amount) - Number(b.min_amount));
    for (let i = 0; i < sortedSlabs.length; i++) {
      const slab = sortedSlabs[i];
      const rate = Number(slab.rate) || 0;
      const maxExclusive = Number(slab.max_amount) || 0;

      if (isInclusive) {
        const inclusiveLimit = maxExclusive + (maxExclusive * (rate / 100));
        if (val <= inclusiveLimit) {
          return slab;
        }
      } else {
        if (val <= maxExclusive) {
          return slab;
        }
      }
    }
    const lastSlabIndex = sortedSlabs.length - 1;
    const lastSlab = sortedSlabs[lastSlabIndex];

    return lastSlab
  };

  getStayCalculation = (checkin, checkout, stayTypeCode, standardCheckinTime, standardCheckoutTime, isRoomShiftedNewRoom = false) => {


    if (!checkin || !this.isValidDate(checkin) || !checkout || !this.isValidDate(checkout) || !stayTypeCode) {

      return null;
    }
    let checkinDate = new Date(checkin);
    let checkoutDate = new Date(checkout);
    let response = {
      checkin: format(checkinDate, "yyyy-MM-dd HH:mm:ss"),
      checkout: format(checkoutDate, "yyyy-MM-dd HH:mm:ss"),
      checkinGraceLabel: null,
      checkoutGraceLabel: null,
      lateCheckoutGrace: null,
      earlyCheckinGrace: null,
      isSameDayGrace: false
    };
    const type = stayTypeCode?.toLowerCase();
    if (type === 'day') {
      const totalMinutes = differenceInMinutes(checkoutDate, checkinDate);
      const checkinMins = checkinDate.getHours() * 60 + checkinDate.getMinutes();
      const checkoutMins = checkoutDate.getHours() * 60 + checkoutDate.getMinutes();

      // Grace label only when checkout time-of-day > check-in time-of-day
      if (checkoutMins > checkinMins) {
        const graceHrs = Math.ceil((checkoutMins - checkinMins) / 60);
        response.checkoutGraceLabel = `${graceHrs}`;
        response.lateCheckoutGrace = `${format(checkoutDate, "dd-MM-yy")}(${graceHrs}hr)`;
      }

      // Separate flag for trim logic — true when API over-returns one phantom row
      response.isExactDayBoundary = (totalMinutes % 1440 === 0);
    }
    else if (type === 'noon') {
      const isSameDay = format(checkinDate, "yyyy-MM-dd") === format(checkoutDate, "yyyy-MM-dd");
      const noonCheckinLimit = new Date(checkinDate); noonCheckinLimit.setHours(12, 0, 0, 0);
      const noonCheckoutLimit = new Date(checkoutDate); noonCheckoutLimit.setHours(12, 0, 0, 0);

      // Same-day, both BEFORE noon -> grace = direct difference, into checkinGraceLabel
      const bothBeforeNoon = isSameDay && checkoutDate <= noonCheckoutLimit;
      // Same-day, both AFTER (or at) noon -> grace = direct difference, into checkoutGraceLabel
      const bothAfterNoon  = isSameDay && checkinDate >= noonCheckinLimit;

      if (bothBeforeNoon) {
        const graceHrs = Math.ceil(differenceInMinutes(checkoutDate, checkinDate) / 60);
        if (graceHrs > 0) {
          response.checkinGraceLabel = graceHrs;
          response.earlyCheckinGrace = `${format(checkinDate, "dd-MM-yy")}(${graceHrs}hr)`;
          response.isSameDayGrace = true;
        }
      } else if (bothAfterNoon) {
        const graceHrs = Math.ceil(differenceInMinutes(checkoutDate, checkinDate) / 60);
        if (graceHrs > 0) {
          response.checkoutGraceLabel = graceHrs;
          response.lateCheckoutGrace = `${format(checkoutDate, "dd-MM-yy")}(${graceHrs}hr)`;
          response.isSameDayGrace = true;
        }
      } else {
        if (checkinDate < noonCheckinLimit) {
          const earlyMins = differenceInMinutes(noonCheckinLimit, checkinDate);
          response.checkinGraceLabel = Math.ceil(earlyMins / 60);
          response.earlyCheckinGrace = `${format(checkinDate, "dd-MM-yy")}(${response.checkinGraceLabel}hr)`;
        }
        if (checkoutDate > noonCheckoutLimit) {
          const lateMins = differenceInMinutes(checkoutDate, noonCheckoutLimit);
          response.checkoutGraceLabel = Math.ceil(lateMins / 60);
          response.lateCheckoutGrace = `${format(checkoutDate, "dd-MM-yy")}(${response.checkoutGraceLabel}hr)`;
        }
      }
    }
    else if (type === 'standard') {
      const isSameDay = format(checkinDate, "yyyy-MM-dd") === format(checkoutDate, "yyyy-MM-dd");
      const ciLimit = this.applyBoundaryTime(checkinDate, standardCheckinTime || '14:00:00');
      const coLimit = this.applyBoundaryTime(checkoutDate, standardCheckoutTime || '12:00:00');

      // Same-day, CO before checkin boundary → guest was early → checkinGraceLabel
      const beforeCiLimit = isSameDay && checkoutDate < ciLimit;
      // Same-day, CI at/after checkin boundary → guest stayed late → checkoutGraceLabel
      const afterCiLimit  = isSameDay && checkinDate >= ciLimit;

      if (beforeCiLimit) {
        const graceHrs = Math.ceil(differenceInMinutes(checkoutDate, checkinDate) / 60);
        if (graceHrs > 0) {
          response.checkinGraceLabel = graceHrs;
          response.earlyCheckinGrace = `${format(checkinDate, "dd-MM-yy")}(${graceHrs}hr)`;
          response.isSameDayGrace = true;
        }
      } else if (afterCiLimit) {
        const graceHrs = Math.ceil(differenceInMinutes(checkoutDate, checkinDate) / 60);
        if (graceHrs > 0) {
          response.checkoutGraceLabel = graceHrs;
          response.lateCheckoutGrace = `${format(checkoutDate, "dd-MM-yy")}(${graceHrs}hr)`;
          response.isSameDayGrace = true;
        }
      } else {
        // EXISTING logic unchanged (boundary cross OR multi-day OR same-day cross-boundary)
        if (checkinDate < ciLimit) {
          const earlyMins = differenceInMinutes(ciLimit, checkinDate);
          response.checkinGraceLabel = Math.ceil(earlyMins / 60);
          response.earlyCheckinGrace = `${format(checkinDate, "dd-MM-yy")}(${response.checkinGraceLabel}hr)`;
        }
        if (checkoutDate > coLimit) {
          const lateMins = differenceInMinutes(checkoutDate, coLimit);
          let graceHrs = Math.ceil(lateMins / 60);
          if (isSameDay) {
            graceHrs = Math.max(0, graceHrs - 2);
          }
          if (graceHrs > 0) {
            response.checkoutGraceLabel = graceHrs;
            response.lateCheckoutGrace = `${format(checkoutDate, "dd-MM-yy")}(${graceHrs}hr)`;
          }
        }
      }
    }

    if (type === 'standard' && isRoomShiftedNewRoom) {
      response.checkinGraceLabel = null;
      response.earlyCheckinGrace = null;
    }

    return response;
  }

  applyGraceLabelsToDayRents = (dayRents, checkin, checkout, stayTypeCode, standardCheckinTime, standardCheckoutTime) => {
    const stayCalc = this.getStayCalculation(checkin, checkout, stayTypeCode, standardCheckinTime, standardCheckoutTime);
    if (!stayCalc || !Array.isArray(dayRents)) return dayRents;

    const dIn = format(new Date(checkin), "yyyy-MM-dd");
    const dOut = format(new Date(checkout), "yyyy-MM-dd");
    //   const checkinDateCounts = dayRents.filter(item =>
    //    format(new Date(item.date), "yyyy-MM-dd") === dIn
    //   ).length;
    // Count occurrences of check-in date to handle Room Shift scenario
    // FIX: Skip already-labeled grace rows (e.g. "16-04-26(1hr)") — new Date() on them returns Invalid Date
    const checkinDateCounts = dayRents.filter(item => {
      if (!item.date || item.date.includes("(")) return false; // Already labeled, skip
      try { return format(new Date(item.date), "yyyy-MM-dd") === dIn; } catch { return false; }
    }).length;

    let hasCheckoutDate = false;
    let appliedCheckinGrace = false;

    const result = dayRents.flatMap((item) => {

      if (item.date && item.date.includes("(")) {
        // This row is already a labeled grace row (e.g. "20-04-26(1hr)").
        // Check if its base date is the checkout date — if so, mark hasCheckoutDate=true
        // to prevent the "Artificially add checkout row" block below from adding a duplicate.
        let basePart = item.date.split("(")[0].trim(); // e.g. "20-04-26" or "20-04-26-"
        if (basePart.endsWith("-")) {
          basePart = basePart.slice(0, -1);
        }
        const parts = basePart.split("-");
        if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 2) {
          const centuryPrefix = new Date().getFullYear().toString().substring(0, 2);
          const normalizedGraceDate = `${centuryPrefix}${parts[2]}-${parts[1]}-${parts[0]}`;
          if (normalizedGraceDate === dOut) {
            hasCheckoutDate = true; // ← prevents duplicate at the bottom
          }
        }
        return item;
      }

      let dItem;
      try { dItem = format(new Date(item.date), "yyyy-MM-dd"); } catch { return item; }

      // Handle Early Check-in Grace: Apply to first occurrence if there are multiple (Room Shift)
      // FIX: Also apply if it's a SameDayGrace and there's only 1 row
      if (dItem === dIn && (checkinDateCounts > 1 || stayCalc.isSameDayGrace) && stayCalc.earlyCheckinGrace && !appliedCheckinGrace) {
        appliedCheckinGrace = true;
        return {
          ...item,
          date: stayCalc.earlyCheckinGrace,
          grace_hours: null // Prevent duplication in EditRent
        };
      }

      // Handle Checkout Grace
      if (dItem === dOut) {
        hasCheckoutDate = true;
        // FIX: Hide normal checkout ONLY if it's a multi-day booking. 
        // For same-day bookings, dOut is the check-in day, so we must NOT hide it!
        if (!stayCalc.checkoutGraceLabel && dIn !== dOut) return []; 
      }

      return {
        ...item,
        date: (dItem === dOut && stayCalc.lateCheckoutGrace)
          ? stayCalc.lateCheckoutGrace
          : item.date,
        grace_hours: (dItem === dOut && stayCalc.lateCheckoutGrace) ? null : item.grace_hours // Prevent duplication
      };
    });

    // Artificially add checkout row if missing but has grace hours
    if (!hasCheckoutDate && stayCalc.checkoutGraceLabel) {
      const lastItem = dayRents[dayRents.length - 1] || {};
      result.push({
        ...lastItem,
        date: stayCalc.lateCheckoutGrace,
        grace_hours: null // Prevent duplication
      });
    }

    return result;
  }
  calculateDayRentValue = (item, existingRow, totalChildChargePerDay) => {


    if (existingRow?.rate_type === "edited") {

      return existingRow;
    }
    const basePrice = Number(item.price || item.amount || 0);
    const totalAmountWithChildren = basePrice + totalChildChargePerDay;

    const baseSource = item.rate_type || item.source;
    const dynamicRateType =
      totalChildChargePerDay > 0 ? `${baseSource}withchildren` : baseSource;

    return {
      date: item.date,
      base_price: basePrice,
      amount: totalAmountWithChildren,
      rate_type: dynamicRateType,
      original_amount: totalAmountWithChildren,
      original_source: baseSource,
    };
  };

  buildDayRents = ({
    breakdown,
    existingDayRents,
    totalChildChargePerDay,
  }) => {
    return breakdown.map((item, index) => {
      const existingRow = existingDayRents?.find((r) => {
        // Fix: DB dates (UTC) and API dates (Local) might have different string dates due to offset.
        // We normalize by converting to local date string (YYYY-MM-DD).
        const rDate = this.getLocalTimeFromUtc(r.date)?.dateString;
        const itemDate = this.getLocalTimeFromUtc(item.date)?.dateString;

        if (index < 5) {

        }

        return rDate === itemDate;
      });

      return this.calculateDayRentValue(
        item,
        existingRow,
        totalChildChargePerDay
      );
    });
  };

  sortDayRents = (dayRents) => {
    if (!Array.isArray(dayRents)) return [];
    return [...dayRents].sort((a, b) => {
      // Normalize dates to YYYY-MM-DD strings for comparison
      const dateA = format(new Date(a.date), "yyyy-MM-dd");
      const dateB = format(new Date(b.date), "yyyy-MM-dd");
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;
      // If dates are identical (e.g. check-in date has both early check-in and normal night),
      // place early check-in (has grace_hours) before normal night (no grace_hours).
      if (a.grace_hours && !b.grace_hours) return -1;
      if (!a.grace_hours && b.grace_hours) return 1;
      return 0;
    });
  };

}
